
import React, { useState, useEffect, useRef } from 'react';
import {
   GitMerge, LayoutTemplate, Plus, Search, Mail, MessageSquare,
   Clock, Zap, Trash2, Edit3, GripVertical, ChevronRight, ChevronDown, Copy, ArrowLeft,
   Sparkles, Save, Play, Bell, Calendar, UserPlus, X, CheckCircle2, AlertCircle, Loader2, Send,
   FileText, ArrowRight, MoveRight, Tag, ClipboardList, StickyNote, BrainCircuit, MousePointer2,
   Bold, Italic, Underline, List, ListOrdered, Phone, Activity, BarChart2, MoreVertical,
   Filter, MessageCircle, AlertTriangle, Image as ImageIcon, Link as LinkIcon, Type, Minus, Star,
   Sliders, BrainCircuit as Brain, RotateCcw, FileSignature, ThumbsUp, MoveRight as StageIcon, FilePlus, Code, GitBranch, Users
} from 'lucide-react';
import { AutomationFlow, AutomationStep, AutomationTemplate, TriggerType, User, AutomationActionType, FlowExecutionLog, AutomationBranch, AutomationConditionGroup } from '../types';
import {
   supabase, supabase_get_flows, supabase_get_templates, supabase_save_flow,
   supabase_delete_flow, supabase_upsert_template, supabase_delete_template,
   extractErrorMessage, supabase_get_client_settings, supabase_update_client_settings, supabase_get_automation_logs
} from '../services/supabaseService';
import { AiMatchingSettings } from '../types';
import { generateTemplateContent } from '../services/geminiService';
import axios from 'axios';
import AskFroukjeAutomationChat from './AskFroukjeAutomationChat';

// --- CONFIGURATION CONSTANTS ---

const TRIGGERS: { id: TriggerType; label: string; icon: any; description: string }[] = [
   { id: 'application_created', label: 'Nieuwe Sollicitatie', icon: <UserPlus size={16} />, description: 'Start wanneer een kandidaat solliciteert.' },
   { id: 'status_rejected', label: 'Kandidaat Afgewezen', icon: <X size={16} />, description: 'Start bij afwijzing van kandidaat.' },
   { id: 'status_interview', label: 'Uitnodiging Interview', icon: <Calendar size={16} />, description: 'Start wanneer status naar Interview gaat.' },
   { id: 'candidate_hired', label: 'Kandidaat Aangenomen', icon: <CheckCircle2 size={16} />, description: 'Start bij succesvolle plaatsing.' },
   { id: 'candidate_birthday', label: 'Verjaardag Kandidaat', icon: <Sparkles size={16} />, description: 'Start op verjaardag (Retentie).' },
   { id: 'vacancy_published', label: 'Vacature Live', icon: <Zap size={16} />, description: 'Start bij publicatie vacature.' },
   { id: 'sla_overdue', label: 'SLA Overschreden', icon: <AlertTriangle size={16} />, description: 'Start als deadline gemist wordt.' },
   { id: 'manual_trigger', label: 'Handmatige Trigger', icon: <MousePointer2 size={16} />, description: 'Start door klik recruiter.' },
   { id: 'positive_review', label: 'Positieve Beoordeling', icon: <ThumbsUp size={16} />, description: 'Start bij een positieve AI of handmatige review.' },
   { id: 'stage_changed', label: 'Fase Gewijzigd', icon: <StageIcon size={16} />, description: 'Start wanneer kandidaat naar een specifieke fase gaat.' },
   { id: 'new_note', label: 'Nieuwe Notitie', icon: <FilePlus size={16} />, description: 'Start wanneer er een interne notitie is toegevoegd.' },
   { id: 'custom_event', label: 'Aangepaste Trigger', icon: <Code size={16} />, description: 'Start via een custom API event hook.' },
   { id: 'candidate_created', label: 'Nieuwe Kandidaat', icon: <UserPlus size={16} />, description: 'Start wanneer een nieuwe kandidaat wordt toegevoegd.' },
   { id: 'candidate_status_changed', label: 'Talentpool Status Gewijzigd', icon: <Users size={16} />, description: 'Start wanneer de status van een kandidaat in de talentpool wijzigt.' },
   { id: 'contract_created', label: 'Nieuw Contract', icon: <FileSignature size={16} />, description: 'Start wanneer een nieuw contract wordt geregistreerd.' },
   { id: 'contract_expiring', label: 'Contract Loopt Af', icon: <Clock size={16} />, description: 'Start wanneer een contract binnenkort afloopt.' },
   { id: 'vacancy_closed', label: 'Vacature Gesloten', icon: <X size={16} />, description: 'Start wanneer een vacature wordt gesloten.' },
   { id: 'meeting_scheduled', label: 'Gesprek Gepland', icon: <Calendar size={16} />, description: 'Start wanneer een (Teams) gesprek is ingepland.' },
];

const ACTIONS = [
   { type: 'email', label: 'Verstuur Email', icon: <Mail size={16} />, category: 'Communication', color: 'red' },
   { type: 'sms', label: 'Verstuur SMS', icon: <MessageSquare size={16} />, category: 'Communication', color: 'emerald' },
   { type: 'whatsapp', label: 'Verstuur WhatsApp', icon: <MessageCircle size={16} />, category: 'Communication', color: 'green' },
   { type: 'branch', label: 'Splitsing (Branch)', icon: <GitBranch size={16} />, category: 'Logic', color: 'indigo' },
   { type: 'condition', label: 'Filter / Conditie', icon: <Filter size={16} />, category: 'Logic', color: 'orange' },
   { type: 'wait', label: 'Wacht', icon: <Clock size={16} />, category: 'Workflow', color: 'amber' },
   { type: 'stage_change', label: 'Verplaats Fase', icon: <MoveRight size={16} />, category: 'Workflow', color: 'purple' },
   { type: 'docusign', label: 'DocuSign Verzoek', icon: <FileSignature size={16} />, category: 'Workflow', color: 'red' },
   { type: 'create_task', label: 'Nieuwe Taak', icon: <ClipboardList size={16} />, category: 'Admin', color: 'slate' },
   { type: 'add_tag', label: 'Labelen (Tag)', icon: <Tag size={16} />, category: 'Admin', color: 'indigo' },
   { type: 'internal_note', label: 'Interne Notitie', icon: <StickyNote size={16} />, category: 'Admin', color: 'yellow' },
   { type: 'notification', label: 'Notificatie', icon: <Bell size={16} />, category: 'Admin', color: 'pink' },
   { type: 'ai_score', label: 'AI Screening', icon: <BrainCircuit size={16} />, category: 'Intelligence', color: 'red' },
   { type: 'n8n_webhook', label: 'n8n Webhook', icon: <Zap size={16} />, category: 'Integration', color: 'orange' },
];

const MOCK_LOGS: FlowExecutionLog[] = [
   { id: '1', flowName: 'Rejection Flow', triggerEvent: 'Candidate Rejected', timestamp: '10:42 AM', status: 'success', stepCount: 3 },
   { id: '2', flowName: 'New Application', triggerEvent: 'App Created', timestamp: '09:15 AM', status: 'success', stepCount: 4 },
   { id: '3', flowName: 'Interview Reminder', triggerEvent: '24h Before', timestamp: 'Yesterday', status: 'failed', stepCount: 2, details: 'SMS Gateway Timeout' },
   { id: '4', flowName: 'Birthday Wish', triggerEvent: 'Date Match', timestamp: 'Yesterday', status: 'success', stepCount: 1 },
];

export const FILTER_OPTIONS = {
   Candidate: [
      { field: 'stage', label: 'Huidige Fase' },
      { field: 'score', label: 'Match Score' },
      { field: 'source', label: 'Bron' },
      { field: 'city', label: 'Woonplaats' },
   ],
   Job: [
      { field: 'title', label: 'Vacature Titel' },
      { field: 'type', label: 'Dienstverband' },
      { field: 'department', label: 'Afdeling' },
      { field: 'location', label: 'Locatie' },
   ],
};

// --- RICH TEXT EDITOR COMPONENT ---
const RichTextEditor = ({ value, onChange, placeholder }: { value: string, onChange: (val: string) => void, placeholder?: string }) => {
   const editorRef = useRef<HTMLDivElement>(null);
   const isInternalUpdate = useRef(false);

   useEffect(() => {
      if (editorRef.current && !isInternalUpdate.current) {
         if (editorRef.current.innerHTML !== value) {
            editorRef.current.innerHTML = value || '';
         }
      }
      isInternalUpdate.current = false;
   }, [value]);

   const handleInput = () => {
      if (editorRef.current) {
         isInternalUpdate.current = true;
         onChange(editorRef.current.innerHTML);
      }
   };

   const exec = (cmd: string, val?: string) => {
      document.execCommand(cmd, false, val);
      if (editorRef.current) editorRef.current.focus();
      handleInput();
   };

   const addImage = () => {
      const url = prompt("Afbeeldings URL:");
      if (url) exec('insertImage', url);
   };

   const addLink = () => {
      const url = prompt("Link URL:");
      if (url) exec('createLink', url);
   };

   return (
      <div className="w-full border border-slate-200 rounded-[1.5rem] overflow-hidden bg-white focus-within:ring-2 focus-within:ring-red-0 transition-all flex flex-col h-64 xl:h-96 shadow-sm">
         <div className="flex items-center gap-1 p-2 border-b border-slate-100 bg-slate-50/50 flex-wrap">
            <div className="flex bg-white rounded-lg border border-slate-200 p-0.5">
               <button onMouseDown={(e) => { e.preventDefault(); exec('bold'); }} className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-900 transition-all" title="Bold"><Bold size={14} /></button>
               <button onMouseDown={(e) => { e.preventDefault(); exec('italic'); }} className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-900 transition-all" title="Italic"><Italic size={14} /></button>
               <button onMouseDown={(e) => { e.preventDefault(); exec('underline'); }} className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-900 transition-all" title="Underline"><Underline size={14} /></button>
            </div>
            <div className="w-px h-6 bg-slate-300 mx-1"></div>
            <div className="flex bg-white rounded-lg border border-slate-200 p-0.5">
               <button onMouseDown={(e) => { e.preventDefault(); exec('formatBlock', 'H2'); }} className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-900 transition-all font-bold text-xs" title="Heading">H1</button>
               <button onMouseDown={(e) => { e.preventDefault(); exec('formatBlock', 'H3'); }} className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-900 transition-all font-bold text-xs" title="Subheading">H2</button>
               <button onMouseDown={(e) => { e.preventDefault(); exec('fontName', 'Arial'); }} className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-900 transition-all" title="Font"><Type size={14} /></button>
            </div>
            <div className="w-px h-6 bg-slate-300 mx-1"></div>
            <div className="flex bg-white rounded-lg border border-slate-200 p-0.5">
               <button onMouseDown={(e) => { e.preventDefault(); exec('insertUnorderedList'); }} className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-900 transition-all" title="Bullet List"><List size={14} /></button>
               <button onMouseDown={(e) => { e.preventDefault(); exec('insertOrderedList'); }} className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-900 transition-all" title="Numbered List"><ListOrdered size={14} /></button>
            </div>
            <div className="w-px h-6 bg-slate-300 mx-1"></div>
            <div className="flex bg-white rounded-lg border border-slate-200 p-0.5">
               <button onMouseDown={(e) => { e.preventDefault(); addLink(); }} className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-900 transition-all" title="Link"><LinkIcon size={14} /></button>
               <button onMouseDown={(e) => { e.preventDefault(); addImage(); }} className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-900 transition-all" title="Image"><ImageIcon size={14} /></button>
               <button onMouseDown={(e) => { e.preventDefault(); exec('insertHorizontalRule'); }} className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-900 transition-all" title="Line"><Minus size={14} /></button>
            </div>
         </div>
         <div
            ref={editorRef}
            className="flex-1 p-6 outline-none overflow-y-auto text-sm leading-relaxed text-slate-700 font-medium prose prose-sm max-w-none prose-p:my-2 prose-headings:mb-2 prose-ul:my-2"
            contentEditable
            onInput={handleInput}
            suppressContentEditableWarning
            data-placeholder={placeholder}
            style={{ fontFamily: 'Inter, sans-serif' }}
         />
      </div>
   );
};

interface AutomationManagerProps {
   user?: User | null;
}

const AutomationManager: React.FC<AutomationManagerProps> = ({ user }) => {
   const [activeTab, setActiveTab] = useState<'flows' | 'templates' | 'logs' | 'drafts' | 'n8n_studio'>('flows');

   // Data State
   const [n8nWorkflows, setN8nWorkflows] = useState<any[]>([]);
   const [flows, setFlows] = useState<AutomationFlow[]>([]);
   const [templates, setTemplates] = useState<AutomationTemplate[]>([]);
   const [logs, setLogs] = useState<any[]>([]);
   const [drafts, setDrafts] = useState<any[]>([]); // New state for drafts
   const [loading, setLoading] = useState(false);
   const [editingDraft, setEditingDraft] = useState<any | null>(null);
   const [isSaving, setIsSaving] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [success, setSuccess] = useState<string | null>(null);

   // Search & Filter State
   const [searchQuery, setSearchQuery] = useState('');
   const [selectedTag, setSelectedTag] = useState<string | null>(null);

   // Editor State
   const [editingFlow, setEditingFlow] = useState<Partial<AutomationFlow> | null>(null);
   const [editingTemplate, setEditingTemplate] = useState<Partial<AutomationTemplate> | null>(null);
   const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null); // For sidebar config
   const [editingFlowTab, setEditingFlowTab] = useState<'canvas' | 'logs'>('canvas');
   const [editingPath, setEditingPath] = useState<{ stepId: string, branchId: string, branchName: string } | null>(null);
   const [expandedLogIdx, setExpandedLogIdx] = useState<number | null>(null);

   const recursivelyUpdateBranchSteps = (steps: AutomationStep[], branchId: string, callback: (steps: AutomationStep[]) => AutomationStep[]): AutomationStep[] => {
      return steps.map(s => {
         if (s.type === 'branch' && s.config.branches) {
            return {
               ...s,
               config: {
                  ...s.config,
                  branches: s.config.branches.map(b => {
                     if (b.id === branchId) return { ...b, steps: callback(b.steps || []) };
                     return { ...b, steps: recursivelyUpdateBranchSteps(b.steps || [], branchId, callback) };
                  })
               }
            };
         }
         return s;
      });
   };

   const findStepDeep = (steps: AutomationStep[], stepId: string): AutomationStep | undefined => {
      for (const s of steps) {
         if (s.id === stepId) return s;
         if (s.type === 'branch' && s.config.branches) {
            for (const b of s.config.branches) {
               const found = findStepDeep(b.steps || [], stepId);
               if (found) return found;
            }
         }
      }
      return undefined;
   };

   // AI State
   const [showAiModal, setShowAiModal] = useState(false);
   const [aiPrompt, setAiPrompt] = useState('');
   const [isGeneratingAi, setIsGeneratingAi] = useState(false); // New state for loader
   const [isGeneratingTrigger, setIsGeneratingTrigger] = useState(false); // New state for trigger names
   const [aiTargetStepId, setAiTargetStepId] = useState<string | null>(null);

   const [showAiFlowModal, setShowAiFlowModal] = useState(false);
   const [addStepIndex, setAddStepIndex] = useState<{ index: number, branchId?: string } | null>(null);

   // DND State
   const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
   const [isDraggingTrigger, setIsDraggingTrigger] = useState(false);
   const [isDraggingAction, setIsDraggingAction] = useState(false);

   // Unique global tags for the selector
   const availableTags = Array.from(new Set(flows.flatMap(f => f.tags || []))).sort();

   useEffect(() => {
      const handleNew = () => {
         handleCreateFlow();
      };
      window.addEventListener('open-new-automation', handleNew);
      return () => window.removeEventListener('open-new-automation', handleNew);
   }, []);

   useEffect(() => {
      fetchData();
   }, []);

   useEffect(() => {
      if (success) {
         const timer = setTimeout(() => setSuccess(null), 3000);
         return () => clearTimeout(timer);
      }
   }, [success]);

   const fetchData = async () => {
      setLoading(true);
      const apiUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? 'http://localhost:4000' : '';
      try {
         if (user?.role === 'ADMIN') {
            const [fData, tData, logData, draftData, n8nRes] = await Promise.all([
               supabase_get_flows(),
               supabase.from('automation_templates').select('*').order('created_at', { ascending: false }),
               supabase.from('automation_logs').select(`*, client:clients(name), flow:automation_flows(name)`).order('created_at', { ascending: false }),
               supabase.from('candidate_communications').select('*, profile:profiles(*)').in('type', ['email_draft', 'sms_draft', 'whatsapp_draft']).order('created_at', { ascending: false }),
               axios.get(`${apiUrl}/api/n8n/workflows`).catch(() => ({ data: { data: [] } }))
            ]);
            setFlows(fData || []);
            setN8nWorkflows(n8nRes?.data?.data || []);
            setTemplates(tData.data || []);
            setLogs((logData.data || []).map(l => ({
               flow_id: l.flow_id,
               flowName: l.flow?.name || l.trigger_event, // Join fallback
               triggerEvent: l.trigger_event,
               status: l.status,
               stepCount: l.step_count,
               timestamp: new Date(l.created_at).toLocaleString('nl-NL'),
               clientName: l.client?.name || 'Algemeen',
               executionTrace: l.execution_trace || []
            })));
            setDrafts(draftData.data || []);
         } else if (user?.clientId || user) {
            const [fData, tData, logData, draftData, n8nRes] = await Promise.all([
               supabase_get_flows(user?.clientId),
               supabase.from('automation_templates').select('*').eq('client_id', user?.clientId).order('created_at', { ascending: false }),
               supabase.from('automation_logs').select(`*, flow:automation_flows(name)`).eq('client_id', user?.clientId).order('created_at', { ascending: false }),
               supabase.from('candidate_communications')
                  .select('*, profile:profiles!inner(*)')
                  .eq('profiles.client_id', user?.clientId)
                  .in('type', ['email_draft', 'sms_draft', 'whatsapp_draft'])
                  .order('created_at', { ascending: false }),
               axios.get(`${apiUrl}/api/n8n/workflows`).catch(() => ({ data: { data: [] } }))
            ]);
            setFlows(fData || []);
            setN8nWorkflows(n8nRes?.data?.data || []);
            setTemplates(tData.data || []);
            setLogs((logData.data || []).map(l => ({
               flow_id: l.flow_id,
               flowName: l.flow?.name || l.trigger_event,
               triggerEvent: l.trigger_event,
               status: l.status,
               stepCount: l.step_count,
               timestamp: new Date(l.created_at).toLocaleString('nl-NL'),
               executionTrace: l.execution_trace || []
            })));
            setDrafts(draftData.data || []);
         }
      } catch (e) {
         console.error(e);
      } finally {
         setLoading(false);
      }
   };

   /* --- FLOW OPERATIONS --- */

   const handleCreateFlow = () => {
      setEditingFlow({
         name: 'Nieuwe Flow',
         trigger: 'application_created',
         status: 'draft',
         steps: [],
         tags: [],
         scope: user?.role === 'ADMIN' ? 'global' : 'client'
      });
      setSelectedNodeId(null);
   };

   const handleSaveFlow = async () => {
      if (!editingFlow || !editingFlow.name) return;
      if (!user?.clientId) {
         setError("Geen Client ID gevonden.");
         return;
      }

      setIsSaving(true);
      setError(null);
      try {
         const flowData = {
            id: editingFlow.id,
            client_id: user.clientId,
            name: editingFlow.name,
            trigger_type: editingFlow.trigger,
            trigger_config: editingFlow.trigger_config,
            status: editingFlow.status,
            tags: editingFlow.tags || []
         };

         const savedFlow = await supabase_save_flow(flowData, editingFlow.steps || []);
         setEditingFlow(prev => prev ? { ...prev, id: savedFlow.id } : null);
         setSuccess("Flow succesvol opgeslagen!");
         await fetchData();
      } catch (e: any) {
         setError(extractErrorMessage(e));
      } finally {
         setIsSaving(false);
      }
   };

   const handleDeleteFlow = async (id: string) => {
      if (!confirm("Weet je zeker dat je deze flow wilt verwijderen?")) return;
      try {
         await supabase_delete_flow(id);
         setFlows(prev => prev.filter(f => f.id !== id));
         if (editingFlow?.id === id) setEditingFlow(null);
      } catch (e: any) {
         alert("Fout bij verwijderen: " + e.message);
      }
   };

   const handleCopyFlow = async (flowToCopy: AutomationFlow) => {
      if (!user?.clientId) return;
      setIsSaving(true);
      try {
         const copiedFlowData = {
            client_id: user.clientId,
            name: `Kopie van ${flowToCopy.name}`,
            trigger_type: flowToCopy.trigger,
            trigger_config: flowToCopy.trigger_config,
            status: 'draft',
            tags: flowToCopy.tags || []
         };

         // Strip ID from steps to create new ones
         const newSteps = (flowToCopy.steps || []).map(s => ({ ...s, id: undefined }));
         const savedFlow = await supabase_save_flow(copiedFlowData, newSteps);
         setSuccess("Flow succesvol gekopieerd!");
         setEditingFlow(savedFlow);
         setEditingFlowTab('canvas');
         await fetchData();
      } catch (e: any) {
         setError("Fout bij kopiëren: " + extractErrorMessage(e));
      } finally {
         setIsSaving(false);
      }
   };

   /* --- FLOW CANVAS OPERATIONS --- */

   const addStepDeep = (type: AutomationStep['type'], dropIndex: number, targetBranchId?: string) => {
      if (!editingFlow) return;
      let label = 'Nieuwe Stap';
      const actionDef = ACTIONS.find(a => a.type === type);
      if (actionDef) label = actionDef.label;

      const newStep: AutomationStep = {
         id: Math.random().toString(),
         type,
         label,
         config: { delay: 1, delayUnit: 'days', smsType: 'custom', taskPriority: 'Medium' }
      };

      if (type === 'branch') {
         newStep.config.branches = [
            { id: Math.random().toString(), name: 'Match', conditionGroups: [], steps: [] },
            { id: Math.random().toString(), name: 'Anders (Default)', conditionGroups: [], steps: [] }
         ];
      }

      const callback = (arr: AutomationStep[]) => {
         const copy = [...arr];
         copy.splice(dropIndex, 0, newStep);
         return copy;
      };

      const newSteps = targetBranchId
         ? recursivelyUpdateBranchSteps(editingFlow.steps || [], targetBranchId, callback)
         : callback(editingFlow.steps || []);

      setEditingFlow({ ...editingFlow, steps: newSteps });
      setSelectedNodeId(newStep.id);
   };

   const updateStepConfigDeep = (stepId: string, updates: any) => {
      if (!editingFlow) return;
      const recurse = (steps: AutomationStep[]): AutomationStep[] => {
         return steps.map(s => {
            if (s.id === stepId) return { ...s, config: { ...s.config, ...updates } };
            if (s.type === 'branch' && s.config.branches) {
               return { ...s, config: { ...s.config, branches: s.config.branches.map(b => ({ ...b, steps: recurse(b.steps || []) })) } };
            }
            return s;
         });
      };
      setEditingFlow({ ...editingFlow, steps: recurse(editingFlow.steps || []) });
   };

   const removeStepDeep = (stepId: string) => {
      if (!editingFlow) return;
      const recurse = (steps: AutomationStep[]): AutomationStep[] => {
         return steps.filter(s => s.id !== stepId).map(s => {
            if (s.type === 'branch' && s.config.branches) {
               return { ...s, config: { ...s.config, branches: s.config.branches.map(b => ({ ...b, steps: recurse(b.steps || []) })) } };
            }
            return s;
         });
      };
      setEditingFlow({ ...editingFlow, steps: recurse(editingFlow.steps || []) });
      if (selectedNodeId === stepId) setSelectedNodeId(null);
   };

   const moveStepDeep = (sourceStepId: string, dropIndex: number, targetBranchId?: string) => {
      if (!editingFlow || !editingFlow.steps) return;
      const sourceStep = findStepDeep(editingFlow.steps, sourceStepId);
      if (!sourceStep) return;

      const stepsWithoutSource = (steps: AutomationStep[]): AutomationStep[] => {
         return steps.filter(s => s.id !== sourceStepId).map(s => {
            if (s.type === 'branch' && s.config.branches) {
               return { ...s, config: { ...s.config, branches: s.config.branches.map(b => ({ ...b, steps: stepsWithoutSource(b.steps || []) })) } };
            }
            return s;
         });
      };
      const tempSteps = stepsWithoutSource(editingFlow.steps);

      const callback = (arr: AutomationStep[]) => {
         const copy = [...arr];
         copy.splice(dropIndex, 0, sourceStep);
         return copy;
      };

      const newSteps = targetBranchId ? recursivelyUpdateBranchSteps(tempSteps, targetBranchId, callback) : callback(tempSteps);
      setEditingFlow({ ...editingFlow, steps: newSteps });
   };

   const handleDragStartTool = (e: React.DragEvent, type: string, kind: 'action' | 'trigger') => {
      e.dataTransfer.setData('application/json', JSON.stringify({ type, kind }));
      if (kind === 'action') setIsDraggingAction(true);
   };

   const handleDragStartStep = (e: React.DragEvent, stepId: string) => {
      e.dataTransfer.setData('application/json', JSON.stringify({ stepId, kind: 'reorder' }));
      setIsDraggingAction(true);
   };

   const handleDragEnd = () => {
      setIsDraggingAction(false);
      setIsDraggingTrigger(false);
      setDragOverIndex(null);
      setAddStepIndex(null);
   };

   const handleDragOver = (e: React.DragEvent, index: number) => {
      e.preventDefault();
      setDragOverIndex(index);
   };

   const handleDropCanvas = (e: React.DragEvent, dropIndex: number, targetBranchId?: string) => {
      e.preventDefault();
      setDragOverIndex(null);
      setIsDraggingAction(false);

      try {
         const payload = JSON.parse(e.dataTransfer.getData('application/json'));
         if (payload.kind === 'action') {
            addStepDeep(payload.type as AutomationStep['type'], dropIndex, targetBranchId);
         } else if (payload.kind === 'reorder') {
            moveStepDeep(payload.stepId, dropIndex, targetBranchId);
         }
      } catch (err) {
         console.warn("Invalid drop payload", err);
      }
      setAddStepIndex(null);
   };

   const handleDropTrigger = (e: React.DragEvent) => {
      e.preventDefault();
      setIsDraggingTrigger(false);
      try {
         const payload = JSON.parse(e.dataTransfer.getData('application/json'));
         if (payload.kind === 'trigger') {
            setEditingFlow(prev => prev ? ({ ...prev, trigger: payload.type as TriggerType }) : null);
         }
      } catch (err) { }
   };

   /* --- TEMPLATE OPERATIONS --- */

   const handleCreateTemplate = () => {
      setEditingTemplate({ name: 'Nieuwe Template', type: 'Email', subject: '', content: '' });
   };

   const handleSaveTemplate = async () => {
      if (!editingTemplate || !editingTemplate.name) return;
      if (!user?.clientId) { setError("Geen Client ID gevonden."); return; }

      setIsSaving(true);
      try {
         const templateData = {
            id: editingTemplate.id,
            client_id: user.clientId,
            name: editingTemplate.name,
            type: editingTemplate.type,
            subject: editingTemplate.subject,
            content: editingTemplate.content
         };

         const saved = await supabase_upsert_template(templateData);
         setEditingTemplate(saved);
         setSuccess("Template opgeslagen!");
         await fetchData();
      } catch (e: any) {
         setError(extractErrorMessage(e));
      } finally {
         setIsSaving(false);
      }
   };

   const handleDeleteTemplate = async (id: string) => {
      if (!confirm("Delete template?")) return;
      try {
         await supabase_delete_template(id);
         setTemplates(prev => prev.filter(t => t.id !== id));
         if (editingTemplate?.id === id) setEditingTemplate(null);
      } catch (e: any) { alert("Error: " + e.message); }
   };

   // Helper
   const getStepIcon = (type: string) => ACTIONS.find(a => a.type === type)?.icon || <Zap size={20} />;
   const getStepColor = (type: string) => ACTIONS.find(a => a.type === type)?.color || 'slate';

   const handleAiGenerate = async () => {
      if (!aiPrompt.trim() || (!editingTemplate && !aiTargetStepId)) return;
      setIsGeneratingAi(true);
      try {
         let type: 'sms' | 'email' = 'email';
         if (editingTemplate) {
            type = (editingTemplate.type === 'SMS' || editingTemplate.type === 'WhatsApp') ? 'sms' : 'email';
         } else if (aiTargetStepId && editingFlow?.steps?.find(s => s.id === aiTargetStepId)) {
            const step = editingFlow.steps.find(s => s.id === aiTargetStepId);
            type = (step?.type === 'sms' || step?.type === 'whatsapp') ? 'sms' : 'email';
         }

         const result = await generateTemplateContent(aiPrompt, type);

         if (editingTemplate) {
            setEditingTemplate(prev => ({
               ...prev,
               subject: result.subject || prev?.subject,
               content: result.content || prev?.content
            }));
         } else if (aiTargetStepId) {
            updateStepConfigDeep(aiTargetStepId, { customMessage: result.content });
         }

         setShowAiModal(false);
         setAiPrompt('');
         setAiTargetStepId(null);
         setSuccess("AI heeft content gegenereerd!");
      } catch (e: any) {
         setError("AI Fout: " + e.message);
      } finally {
         setIsGeneratingAi(false);
      }
   };

   const handleFlowGenerated = (result: any) => {
      const newFlow: Partial<AutomationFlow> = {
         name: result.name || 'AI Genereerde Flow',
         description: result.description,
         trigger: (result.trigger as TriggerType) || 'application_created',
         status: 'draft',
         scope: user?.role === 'ADMIN' ? 'global' : 'client',
         tags: ['AI Generated'],
         steps: (result.steps || []).map((s: any, idx: number) => ({
            id: Math.random().toString(),
            type: (s.type as AutomationActionType) || 'email',
            label: s.label || `Stap ${idx + 1}`,
            config: s.config || {}
         }))
      };

      setEditingFlow(newFlow);
      setSuccess("Froukje heeft een flow voor je klaargezet in het canvas!");
      // We don't close the modal automatically; the user can keep chatting if they want.
   };

   const handleSendDraft = async (draft: any) => {
      try {
         const phone = draft.profile?.candidate?.phone;
         const email = draft.profile?.candidate?.email;

         if (draft.type === 'whatsapp_draft') {
            if (phone) window.open(`https://wa.me/${phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(draft.content || '')}`, '_blank');
            else return alert("Geen telefoonnummer gevonden voor deze kandidaat.");
         } else if (draft.type === 'email_draft') {
            if (email) window.location.href = `mailto:${email}?subject=${encodeURIComponent(draft.subject || 'Bericht')}&body=${encodeURIComponent(draft.content || '')}`;
            else return alert("Geen e-mailadres gevonden.");
         } else if (draft.type === 'sms_draft') {
            if (phone) window.location.href = `sms:${phone.replace(/[^0-9]/g, '')}?body=${encodeURIComponent(draft.content || '')}`;
            else return alert("Geen telefoonnummer gevonden.");
         }

         const newType = draft.type.replace('_draft', '_sent');
         await supabase.from('candidate_communications').update({ type: newType }).eq('id', draft.id);

         setDrafts(prev => prev.filter(d => d.id !== draft.id));
         setSuccess("Concept geopend en wachten op verzending!");
      } catch (err: any) {
         setError("Fout bij openen draft: " + err.message);
      }
   };

   return (
      <div className="flex flex-col h-[calc(100vh-80px)] p-4 xl:p-6 max-w-[1800px] mx-auto animate-fadeIn relative">

         {/* HEADER BAR */}
         <div className="flex items-center justify-between shrink-0 mb-4 xl:mb-6">
            <div>
               <h1 className="text-xl font-black text-slate-900 tracking-tight leading-tight flex items-center gap-2">
                  <img src="https://xquintpfpzdsxupnhypj.supabase.co/storage/v1/object/public/public/website/Werftools.png" alt="Werftools" className="h-6 object-contain shrink-0" />
                  Automation Engine
               </h1>
               <p className="text-xs text-slate-500 font-medium mt-1">Bouw slimme workflows en omnichannel communicatie.</p>
            </div>
            <div className="flex gap-3">
               {[
                  { id: 'flows', label: 'Flow Builder', icon: <GitMerge size={16} /> },
                  { id: 'templates', label: 'Template Library', icon: <LayoutTemplate size={16} /> },
                  { id: 'drafts', label: 'Klaarzetten (Concept)', icon: <Send size={16} /> },
                  { id: 'logs', label: 'Activity Logs', icon: <Activity size={16} /> }
               ].map(tab => (
                  <button
                     key={tab.id}
                     onClick={() => { setActiveTab(tab.id as any); setEditingFlow(null); setEditingTemplate(null); }}
                     className={`flex items-center gap-2 px-4 xl:px-6 py-2 xl:py-2.5 rounded-lg text-xs font-black transition-all uppercase tracking-widest ${activeTab === tab.id ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'}`}
                  >
                     {tab.icon} {tab.label}
                  </button>
               ))}
            </div>
         </div>

         {error && <div className="bg-red-50 border border-red-100 text-red-600 p-4 rounded-xl flex items-center gap-3 mb-6"><AlertCircle size={20} /><span className="text-xs font-bold">{error}</span></div>}
         {success && <div className="bg-emerald-50 border border-emerald-100 text-emerald-600 p-4 rounded-xl flex items-center gap-3 mb-6"><CheckCircle2 size={20} /><span className="text-xs font-bold">{success}</span></div>}

         {/* CONTENT AREA */}
         <div className="flex-1 overflow-hidden relative">

            {/* LOGS VIEW */}
            {activeTab === 'logs' && (
               <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden h-full flex flex-col">
                  <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                     <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">Recent Executions</h3>
                     <button className="text-slate-400 hover:text-slate-900"><Filter size={16} /></button>
                  </div>
                  <div className="flex-1 overflow-y-auto">
                     <table className="w-full text-left">
                        <thead className="bg-slate-50 text-[10px] font-black uppercase text-slate-400">
                           <tr>
                              <th className="px-6 py-4">Flow Name</th>
                              <th className="px-6 py-4">Trigger</th>
                              <th className="px-6 py-4">Timestamp</th>
                              <th className="px-6 py-4">Status</th>
                              <th className="px-6 py-4">Steps</th>
                           </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                           {logs.map((log, i) => (
                              <tr key={i} className="hover:bg-slate-50 transition-colors">
                                 <td className="px-6 py-4 font-bold text-xs text-slate-900">{log.flowName}</td>
                                 <td className="px-6 py-4 text-xs text-slate-500">{log.triggerEvent}</td>
                                 <td className="px-6 py-4 text-xs text-slate-400 font-mono">{log.timestamp}</td>
                                 <td className="px-6 py-4">
                                    <span className={`px-2 py-1 rounded text-[10px] font-black uppercase tracking-wide border ${log.status === 'success' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-red-50 text-red-600 border-red-100'}`}>
                                       {log.status}
                                    </span>
                                 </td>
                                 <td className="px-6 py-4 text-xs font-bold">{log.stepCount}</td>
                              </tr>
                           ))}
                        </tbody>
                     </table>
                  </div>
               </div>
            )}

            {/* TEMPLATES VIEW */}
            {activeTab === 'templates' && (
               <div className="grid grid-cols-12 gap-4 xl:gap-6 h-full">
                  <div className="col-span-4 xl:col-span-3 bg-white rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                     <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                        <h3 className="text-xs font-black uppercase tracking-widest text-slate-900">Templates</h3>
                        <button onClick={handleCreateTemplate} className="w-8 h-8 flex items-center justify-center bg-slate-900 text-white rounded-lg hover:bg-black"><Plus size={16} /></button>
                     </div>
                     <div className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar">
                        {templates.map(tmpl => {
                           if (tmpl.type === 'Section') return null; // hide sections here
                           return (
                              <div key={tmpl.id} onClick={() => setEditingTemplate(tmpl)} className={`p-4 rounded-xl cursor-pointer border transition-all ${editingTemplate?.id === tmpl.id ? 'bg-red- border-red- shadow-sm' : 'bg-white border-slate-100 hover:border-slate-300'}`}>
                                 <div className="flex items-center gap-3">
                                    <div className={`p-2 rounded-lg ${tmpl.type === 'Email' ? 'bg-red- text-red-' : 'bg-emerald-100 text-emerald-600'}`}>
                                       {tmpl.type === 'Email' ? <Mail size={14} /> : tmpl.type === 'WhatsApp' ? <MessageCircle size={14} /> : <MessageSquare size={14} />}
                                    </div>
                                    <div className="min-w-0">
                                       <h4 className="text-xs font-bold text-slate-900 truncate">{tmpl.name}</h4>
                                       <p className="text-[10px] text-slate-400 truncate">{tmpl.subject || 'No Subject'}</p>
                                    </div>
                                 </div>
                              </div>
                           )
                        })}
                     </div>
                  </div>
                  <div className="col-span-8 xl:col-span-9 bg-white rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                     {editingTemplate ? (
                        <div className="flex flex-col h-full">
                           <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                              <input type="text" value={editingTemplate.name} onChange={e => setEditingTemplate({ ...editingTemplate, name: e.target.value })} className="bg-transparent text-lg font-black text-slate-900 outline-none w-full" placeholder="Template Name" />
                              <div className="flex gap-2">
                                 <button onClick={() => setEditingTemplate(null)} className="px-4 py-2 text-slate-500 text-xs font-bold hover:text-slate-900">Cancel</button>
                                 <button onClick={handleSaveTemplate} className="px-6 py-2 bg-slate-900 text-white rounded-lg text-xs font-black uppercase tracking-widest hover:bg-black">{isSaving ? <Loader2 className="animate-spin" size={14} /> : 'Save'}</button>
                              </div>
                           </div>
                           <div className="p-8 flex-1 overflow-y-auto space-y-6">
                              <div className="flex gap-4">
                                 <div className="w-1/3">
                                    <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-2">Type</label>
                                    <select value={editingTemplate.type} onChange={e => setEditingTemplate({ ...editingTemplate, type: e.target.value as any })} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold outline-none">
                                       <option value="Email">Email</option>
                                       <option value="SMS">SMS</option>
                                       <option value="WhatsApp">WhatsApp</option>
                                    </select>
                                 </div>
                                 {editingTemplate.type === 'Email' && (
                                    <div className="flex-1">
                                       <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-2">Subject</label>
                                       <input type="text" value={editingTemplate.subject} onChange={e => setEditingTemplate({ ...editingTemplate, subject: e.target.value })} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold outline-none" />
                                    </div>
                                 )}
                              </div>
                              <div>
                                 <div className="flex justify-between items-center mb-2">
                                    <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block pl-1">Content</label>
                                    <button
                                       onClick={() => setShowAiModal(true)}
                                       className="flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg text-[10px] font-black uppercase tracking-wider hover:shadow-lg hover:scale-105 transition-all"
                                    >
                                       <Sparkles size={12} /> AI Schrijven
                                    </button>
                                 </div>
                                 <RichTextEditor value={editingTemplate.content || ''} onChange={val => setEditingTemplate({ ...editingTemplate, content: val })} placeholder="Start typing or use AI..." />
                                 <div className="flex gap-2 mt-2 flex-wrap">
                                    {['{{candidate.first_name}}', '{{job.title}}', '{{company.name}}', '{{recruiter.name}}'].map(v => (
                                       <span key={v} className="px-2 py-1 bg-slate-100 border border-slate-200 rounded text-[9px] font-mono text-slate-500 cursor-pointer hover:bg-slate-200">{v}</span>
                                    ))}
                                 </div>
                              </div>
                           </div>
                        </div>
                     ) : (
                        <div className="flex items-center justify-center h-full text-slate-300 flex-col gap-4">
                           <LayoutTemplate size={48} />
                           <p className="text-xs font-bold uppercase tracking-widest">Select or create a template</p>
                        </div>
                     )}
                  </div>
               </div>
            )}

            {/* DRAFTS VIEW (Klaarzetten) */}
            {activeTab === 'drafts' && (
               <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden h-full flex flex-col">
                  <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-orange-50/30">
                     <div>
                        <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">Klaarzetten (Concepten)</h3>
                        <p className="text-xs text-slate-500 font-medium">Communicatie die door een flow op 'Manual' is gezet en nog verstuurd moet worden.</p>
                     </div>
                  </div>
                  <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
                     {drafts.length === 0 ? (
                        <div className="flex flex-col items-center justify-center text-center h-full">
                           <div className="w-20 h-20 bg-orange-100 text-orange-500 rounded-full flex items-center justify-center mb-6 shadow-sm">
                              <Send size={32} />
                           </div>
                           <h4 className="text-lg font-black text-slate-900 mb-2">Momenteel geen concepten</h4>
                           <p className="text-sm text-slate-500 max-w-sm">Wanneer een automation op 'Zet Klaar (Concept)' staat ingesteld, verschijnen de gegenereerde e-mails of berichten hier ter goedkeuring voordat ze naar de kandidaat gaan.</p>
                        </div>
                     ) : (
                        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                           <table className="w-full text-left border-collapse">
                              <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase text-slate-500 tracking-wider">
                                 <tr>
                                    <th className="px-6 py-4">Status & Type</th>
                                    <th className="px-6 py-4">Kandidaat</th>
                                    <th className="px-6 py-4">Aangemaakt Op</th>
                                    <th className="px-6 py-4">Voorbeeld Bericht</th>
                                    <th className="px-6 py-4 text-right">Acties</th>
                                 </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                 {drafts.map(draft => (
                                    <tr key={draft.id} className="hover:bg-slate-50 transition-colors">
                                       <td className="px-6 py-4">
                                          <div className="flex flex-col gap-1 items-start">
                                             <span className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-wider rounded-md w-fit flex items-center gap-1.5 ${draft.type.includes('email') ? 'bg-red- text-red-' : draft.type.includes('whatsapp') ? 'bg-emerald-100 text-emerald-700' : 'bg-purple-100 text-purple-700'}`}>
                                                {draft.type.includes('email') ? <Mail size={12} /> : draft.type.includes('whatsapp') ? <MessageCircle size={12} /> : <MessageSquare size={12} />}
                                                {draft.type.includes('email') ? 'Email' : draft.type.includes('whatsapp') ? 'WhatsApp' : 'SMS'} Concept
                                             </span>
                                          </div>
                                       </td>
                                       <td className="px-6 py-4 text-sm font-bold text-slate-900">
                                          {draft.profile?.candidate?.name || 'Onbekend'}
                                       </td>
                                       <td className="px-6 py-4 text-[11px] font-mono text-slate-500">
                                          {new Date(draft.created_at).toLocaleString('nl-NL')}
                                       </td>
                                       <td className="px-6 py-4 max-w-xs">
                                          <div className="text-xs text-slate-600 font-medium truncate">
                                             {draft.content.split('\n').pop()}
                                          </div>
                                       </td>
                                       <td className="px-6 py-4 flex gap-2 justify-end">
                                          <button onClick={() => setEditingDraft(draft)} className="p-2 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors group relative">
                                             <Edit3 size={16} />
                                             <span className="absolute bottom-full right-0 mb-2 hidden group-hover:block bg-slate-900 text-white text-[10px] font-bold px-2 py-1 rounded whitespace-nowrap">Bewerk (Ook WhatsApp)</span>
                                          </button>
                                          <button onClick={() => handleSendDraft(draft)} className="px-4 py-2 text-[10px] font-black uppercase tracking-wider text-white bg-slate-900 rounded-lg hover:bg-black transition-colors flex items-center gap-1.5 shadow-md hover:shadow-lg">
                                             <Send size={12} /> Verstuur
                                          </button>
                                       </td>
                                    </tr>
                                 ))}
                              </tbody>
                           </table>
                        </div>
                     )}
                  </div>
               </div>
            )}

            {/* FLOW BUILDER VIEW */}
            {activeTab === 'flows' && (
               <div className="h-full">
                  {!editingFlow ? (
                     <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden h-full flex flex-col">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                           <div className="flex gap-4 items-center">
                              <div className="bg-white border border-slate-200 rounded-lg px-4 py-2 flex items-center gap-2 text-xs font-bold text-slate-700 cursor-pointer hover:border-slate-300 transition-colors shadow-sm">
                                 Groups <span className="text-red- font-medium ml-1">Manage</span> <ChevronDown size={14} className="text-slate-400 ml-2" />
                              </div>
                           </div>
                           <div className="flex gap-4 items-center">
                              <div className="relative w-72">
                                 <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                                 <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search Automations"
                                    className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2 text-xs font-medium outline-none focus:border-slate-400 focus:ring-2 focus:ring-red- transition-all placeholder:font-normal shadow-sm"
                                 />
                              </div>
                              <button onClick={() => { if (!editingFlow) handleCreateFlow(); setShowAiFlowModal(true); }} className="flex items-center gap-2 h-9 px-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg hover:shadow-md transition-all whitespace-nowrap"><Sparkles size={14} /><span className="text-[10px] font-bold uppercase tracking-widest">AI Flow</span></button>
                              <button onClick={() => window.open('http://automation.werftools.nl', '_blank')} className="h-9 px-4 flex items-center justify-center bg-orange-600 hover:bg-orange-700 text-white rounded-lg shadow-md text-[10px] font-bold uppercase tracking-widest gap-2 transition-colors"><Zap size={14} /> n8n Workflow</button>
                              <button onClick={handleCreateFlow} className="h-9 px-4 flex items-center justify-center bg-slate-900 text-white rounded-lg hover:bg-black shadow-md text-[10px] font-bold uppercase tracking-widest gap-2"><Plus size={14} /> Nieuw</button>
                           </div>
                        </div>

                        <div className="flex-1 overflow-y-auto custom-scrollbar">
                           <table className="w-full text-left border-collapse">
                              <thead className="bg-white border-b border-slate-200 text-[10px] font-black uppercase text-slate-400 tracking-wider sticky top-0 z-10">
                                 <tr>
                                    <th className="px-6 py-4 w-12"><input type="checkbox" className="rounded border-slate-300" /></th>
                                    <th className="px-6 py-4 font-black">STATUS</th>
                                    <th className="px-6 py-4 font-black">AUTOMATION NAME</th>
                                    <th className="px-6 py-4 font-black">ACTIVE COUNT</th>
                                    <th className="px-6 py-4 font-black">TOTAL COUNT</th>
                                    <th className="px-6 py-4 font-black"># OF STEPS</th>
                                    <th className="px-6 py-4 font-black">GROUPS</th>
                                    <th className="px-6 py-4 font-black text-right">ACTIONS</th>
                                 </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                 {loading ? (
                                    <tr><td colSpan={8} className="text-center py-10"><Loader2 className="animate-spin text-slate-300 mx-auto" /></td></tr>
                                 ) : (flows.length === 0 && (!n8nWorkflows || n8nWorkflows.length === 0)) ? (
                                    <tr>
                                       <td colSpan={8} className="text-center py-16 text-slate-400">
                                          <GitMerge size={32} className="mx-auto mb-4 opacity-50" />
                                          <p className="font-medium text-sm">Geen automations gevonden.</p>
                                          <button onClick={handleCreateFlow} className="mt-4 text-red-0 font-bold text-xs hover:underline">Maak je eerste automation</button>
                                       </td>
                                    </tr>
                                 ) : flows
                                    .filter(f => !searchQuery || f.name.toLowerCase().includes(searchQuery.toLowerCase()))
                                    .map((flow) => {
                                       const flowLogs = logs.filter(l => l.flow_id === flow.id || l.flowName === flow.name);
                                       const totalCount = flowLogs.length;
                                       const activeCount = flowLogs.filter(l => l.status === 'success').length; // In a true live system, "Active" might mean running, but we use success here as a proxy for completed/active instances

                                       return (
                                          <tr key={flow.id} className="hover:bg-slate-50 transition-colors group cursor-pointer" onClick={() => { setEditingFlow(flow); setSelectedNodeId(null); setEditingFlowTab('canvas'); }}>
                                             <td className="px-6 py-4" onClick={e => e.stopPropagation()}><input type="checkbox" className="rounded border-slate-300" /></td>
                                             <td className="px-6 py-4">
                                                <span className={`inline-block px-2.5 py-1 border rounded text-[10px] font-bold uppercase tracking-widest ${flow.status === 'active' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>{flow.status}</span>
                                             </td>
                                             <td className="px-6 py-4">
                                                <div className="font-bold text-sm text-red- group-hover:underline">{flow.name}</div>
                                                <div className="text-[10px] text-slate-400 font-medium flex items-center gap-1.5 mt-1">
                                                   <div className="w-1.5 h-1.5 rounded-full bg-red-"></div> {TRIGGERS.find(t => t.id === flow.trigger)?.label || 'Trigger-based'}
                                                </div>
                                             </td>
                                             <td className="px-6 py-4 text-sm font-medium text-slate-700">{activeCount}</td>
                                             <td className="px-6 py-4 text-sm font-medium text-slate-700">{totalCount}</td>
                                             <td className="px-6 py-4 text-sm font-medium text-slate-700">{flow.steps?.length || 0}</td>
                                             <td className="px-6 py-4">
                                                <div className="flex gap-2 flex-wrap max-w-xs">
                                                   {(flow.tags || []).slice(0, 3).map(tag => (
                                                      <span key={tag} className="text-[9px] font-bold text-slate-700 uppercase tracking-widest border border-slate-200 bg-white rounded-full px-2.5 py-1 shadow-sm">
                                                         {tag}
                                                      </span>
                                                   ))}
                                                   {(flow.tags?.length || 0) > 3 && (
                                                      <span className="text-[9px] font-bold text-slate-400 border border-slate-200 bg-slate-50 rounded-full px-2 py-1">
                                                         +{flow.tags!.length - 3}
                                                      </span>
                                                   )}
                                                   {(!flow.tags || flow.tags.length === 0) && <span className="text-[10px] text-slate-300 italic">Geen tags</span>}
                                                </div>
                                             </td>
                                             <td className="px-6 py-4 text-right" onClick={e => e.stopPropagation()}>
                                                <div className="relative inline-block text-left">
                                                   <button className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded text-xs font-bold text-slate-600 transition-colors">
                                                      Acties <ChevronDown size={12} className="text-slate-400" />
                                                   </button>
                                                   <div className="absolute right-0 mt-2 w-48 bg-white border border-slate-100 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 overflow-hidden transform origin-top-right scale-95 group-hover:scale-100">
                                                      <div className="py-1">
                                                         <button onClick={() => { setEditingFlow(flow); setEditingFlowTab('logs'); }} className="w-full text-left px-4 py-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-3"><BarChart2 size={14} className="text-slate-400" /> Bekijk Metrics</button>
                                                         <button onClick={() => handleCopyFlow(flow)} className="w-full text-left px-4 py-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-3"><Copy size={14} className="text-slate-400" /> Kopieer Flow</button>
                                                         <button onClick={() => handleDeleteFlow(flow.id)} className="w-full text-left px-4 py-2.5 text-xs font-medium text-red-600 hover:bg-red-50 flex items-center gap-3"><Trash2 size={14} className="text-red-400" /> Verwijder Flow</button>
                                                         <div className="h-px bg-slate-100 my-1"></div>
                                                         <button onClick={() => { setEditingFlow(flow); setEditingFlowTab('canvas'); }} className="w-full text-left px-4 py-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-3"><Tag size={14} className="text-slate-400" /> Bewerk Tags</button>
                                                      </div>
                                                   </div>
                                                </div>
                                             </td>
                                          </tr>
                                       )
                                    })}
                                 {n8nWorkflows && n8nWorkflows
                                    .filter(f => !searchQuery || f.name.toLowerCase().includes(searchQuery.toLowerCase()))
                                    .map((flow) => {
                                       return (
                                          <tr key={flow.id} className="hover:bg-orange-50/50 transition-colors group cursor-pointer" onClick={() => window.open('http://automation.werftools.nl', '_blank')}>
                                             <td className="px-6 py-4" onClick={e => e.stopPropagation()}><input type="checkbox" className="rounded border-slate-300" disabled /></td>
                                             <td className="px-6 py-4">
                                                <span className={`inline-block px-2.5 py-1 border rounded text-[10px] font-bold uppercase tracking-widest ${flow.active ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>{flow.active ? 'active' : 'inactive'}</span>
                                             </td>
                                             <td className="px-6 py-4">
                                                <div className="font-bold text-sm text-orange-600 group-hover:underline flex items-center gap-2"><Zap size={14} /> {flow.name}</div>
                                                <div className="text-[10px] text-slate-400 font-medium mt-1">
                                                   Beheerd in externe n8n instantie
                                                </div>
                                             </td>
                                             <td className="px-6 py-4 text-sm font-medium text-slate-700">-</td>
                                             <td className="px-6 py-4 text-sm font-medium text-slate-700">-</td>
                                             <td className="px-6 py-4 text-sm font-medium text-slate-700">N/A</td>
                                             <td className="px-6 py-4">
                                                <div className="flex gap-2 flex-wrap max-w-xs">
                                                   {(flow.tags || []).slice(0, 3).map((tag: any) => (
                                                      <span key={tag.id || tag.name || tag} className="text-[9px] font-bold text-orange-700 uppercase tracking-widest border border-orange-200 bg-white rounded-full px-2.5 py-1 shadow-sm">
                                                         {tag.name || tag}
                                                      </span>
                                                   ))}
                                                   {(!flow.tags || flow.tags.length === 0) && <span className="text-[10px] font-bold text-orange-700 uppercase tracking-widest border border-orange-200 bg-orange-50 rounded-full px-2.5 py-1 shadow-sm">n8n Node</span>}
                                                </div>
                                             </td>
                                             <td className="px-6 py-4 text-right" onClick={e => e.stopPropagation()}>
                                                <button onClick={() => window.open('http://automation.werftools.nl', '_blank')} className="flex items-center gap-2 ml-auto px-3 py-1.5 bg-white hover:bg-slate-50 border border-slate-200 rounded text-xs font-bold text-slate-600 transition-colors shadow-sm">
                                                   Open n8n <ChevronRight size={12} className="text-slate-400" />
                                                </button>
                                             </td>
                                          </tr>
                                       )
                                    })}
                              </tbody>
                           </table>
                        </div>
                     </div>
                  ) : (
                     <div className="grid grid-cols-12 gap-4 xl:gap-6 h-full">
                        {/* Center Canvas */}
                        <div className="col-span-12 bg-white rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden flex relative">
                           {/* Left Palette (Tools) */}
                           {editingFlowTab === 'canvas' && (
                              <div className="w-56 xl:w-64 bg-slate-50 border-r border-slate-200 flex flex-col z-10 shrink-0">
                                 <div className="p-4 border-b border-slate-200"><h3 className="text-xs font-black uppercase tracking-widest text-slate-900">Toolbox</h3></div>
                                 <div className="flex-1 overflow-y-auto p-4 space-y-6 custom-scrollbar">
                                    {/* Triggers */}
                                    <div>
                                       <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-2 pl-1">Triggers</label>
                                       <div className="space-y-2">
                                          {TRIGGERS.map(t => (
                                             <div key={t.id} draggable onDragStart={(e) => handleDragStartTool(e, t.id, 'trigger')} onDragEnd={handleDragEnd} className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-xl hover:border-slate-400 hover:shadow-md cursor-grab active:cursor-grabbing transition-all">
                                                <div className="p-1.5 bg-slate-50 rounded text-slate-500">{t.icon}</div>
                                                <span className="text-[10px] font-bold text-slate-700">{t.label}</span>
                                             </div>
                                          ))}
                                       </div>
                                    </div>
                                    {/* Actions */}
                                    {['Communication', 'Logic', 'Workflow', 'Admin', 'Intelligence', 'Integration'].map(cat => {
                                       const items = ACTIONS.filter(a => a.category === cat);
                                       if (!items.length) return null;
                                       return (
                                          <div key={cat}>
                                             <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-2 pl-1">{cat}</label>
                                             <div className="space-y-2">
                                                {items.map(a => (
                                                   <div key={a.type} draggable onDragStart={(e) => handleDragStartTool(e, a.type, 'action')} onDragEnd={handleDragEnd} className={`flex items-center gap-3 p-3 bg-${a.color}-50 border border-${a.color}-100 rounded-xl hover:shadow-md cursor-grab active:cursor-grabbing transition-all group`}>
                                                      <div className="p-1.5 bg-white rounded text-slate-500 group-hover:text-slate-900">{a.icon}</div>
                                                      <span className={`text-[10px] font-bold text-${a.color}-900`}>{a.label}</span>
                                                   </div>
                                                ))}
                                             </div>
                                          </div>
                                       )
                                    })}
                                 </div>
                              </div>
                           )}

                           {/* Center Canvas */}
                           <div className="flex-1 bg-slate-50/50 relative overflow-hidden flex flex-col">
                              {/* Toolbar */}
                              <div className="absolute top-0 left-0 right-0 py-3 bg-white/80 backdrop-blur-md border-b border-slate-200 flex flex-col justify-center px-6 z-20 shadow-sm">
                                 <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-4 w-1/2">
                                       <button
                                          onClick={() => { setEditingFlow(null); setEditingFlowTab('canvas'); }}
                                          className="flex items-center justify-center p-2 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-900 transition-colors"
                                          title="Back to Flows"
                                       >
                                          <ArrowLeft size={18} />
                                       </button>
                                       <input value={editingFlow.name} onChange={e => setEditingFlow({ ...editingFlow, name: e.target.value })} className="bg-transparent text-lg font-black text-slate-900 outline-none w-full" placeholder="Flow Name" />

                                       <div className="flex bg-slate-100 p-1 rounded-lg shrink-0">
                                          <button onClick={() => setEditingFlowTab('canvas')} className={`px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${editingFlowTab === 'canvas' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>Canvas</button>
                                          <button onClick={() => setEditingFlowTab('logs')} className={`px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${editingFlowTab === 'logs' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>Activiteit</button>
                                       </div>
                                    </div>

                                    <div className="flex gap-2">
                                       <select value={editingFlow.status} onChange={e => setEditingFlow({ ...editingFlow, status: e.target.value as any })} className="bg-slate-100 border-0 rounded-lg text-xs font-bold px-3 py-2 outline-none">
                                          <option value="draft">Draft</option>
                                          <option value="active">Active</option>
                                          <option value="paused">Paused</option>
                                       </select>
                                       <button onClick={handleSaveFlow} disabled={isSaving} className="bg-slate-900 text-white px-5 py-2 rounded-lg text-xs font-black uppercase tracking-widest hover:bg-black flex items-center gap-2 transition-all">
                                          {isSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
                                       </button>
                                    </div>
                                 </div>

                                 <div className="flex items-start gap-3 mt-1 relative">
                                    <Tag size={12} className="text-slate-400 mt-2" />
                                    <div className="flex-1 flex flex-col gap-2">
                                       <div className="flex flex-wrap gap-1.5 min-h-[32px] bg-slate-50 border border-slate-200 rounded-xl p-1.5 items-center">
                                          {editingFlow.tags?.map(tag => (
                                             <span key={tag} className="flex items-center gap-1 bg-slate-900 text-white px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider group">
                                                {tag}
                                                <X
                                                   size={10}
                                                   className="cursor-pointer text-slate-400 group-hover:text-red-400 transition-colors"
                                                   onClick={() => setEditingFlow({ ...editingFlow, tags: editingFlow.tags?.filter(t => t !== tag) })}
                                                />
                                             </span>
                                          ))}
                                          <input
                                             type="text"
                                             placeholder={editingFlow.tags?.length ? "Tags toevoegen..." : "Typ een tag en druk op Enter..."}
                                             className="flex-1 min-w-[120px] bg-transparent text-[10px] font-bold text-slate-900 outline-none px-2"
                                             onKeyDown={(e) => {
                                                if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                                                   e.preventDefault();
                                                   const newTag = e.currentTarget.value.trim().toLowerCase();
                                                   if (!editingFlow.tags?.includes(newTag)) {
                                                      setEditingFlow({ ...editingFlow, tags: [...(editingFlow.tags || []), newTag] });
                                                   }
                                                   e.currentTarget.value = '';
                                                }
                                             }}
                                          />
                                       </div>
                                       {/* Existing Tags Suggestions */}
                                       {availableTags.length > 0 && (
                                          <div className="flex flex-wrap gap-1.5 items-center">
                                             <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Of kies bestaande:</span>
                                             {availableTags.filter(t => !editingFlow.tags?.includes(t)).map(tag => (
                                                <button
                                                   key={tag}
                                                   onClick={() => setEditingFlow({ ...editingFlow, tags: [...(editingFlow.tags || []), tag] })}
                                                   className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-md border bg-white text-slate-500 border-slate-200 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200 transition-colors"
                                                >
                                                   +{tag}
                                                </button>
                                             ))}
                                          </div>
                                       )}
                                    </div>
                                 </div>
                              </div>

                              {/* Visualizer */}
                              {editingFlowTab === 'canvas' && (
                                 <div className="flex-1 overflow-y-auto p-10 pt-32 custom-scrollbar relative">
                                    <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(#64748b 1px, transparent 1px)', backgroundSize: '24px 24px' }}></div>

                                    <div className="flex flex-col items-center max-w-lg mx-auto pb-40 relative z-10">
                                       {/* Breadcrumb Navigation when viewing a nested branch */}
                                       {editingPath && (
                                          <div className="w-full mb-8 bg-slate-900 text-white rounded-2xl p-4 flex items-center justify-between shadow-lg">
                                             <div className="flex items-center gap-3">
                                                <button onClick={() => setEditingPath(null)} className="p-2 bg-white/10 rounded-xl hover:bg-white/20 transition-all"><ArrowLeft size={16} /></button>
                                                <div>
                                                   <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Drill-Down Weergave</p>
                                                   <h4 className="font-bold text-sm flex items-center gap-2"><GitBranch size={14} className="text-indigo-400" /> Splisting: {editingPath.branchName}</h4>
                                                </div>
                                             </div>
                                          </div>
                                       )}

                                       {/* Trigger Node - Only visibly root level */}
                                       {!editingPath && (
                                          <div
                                             onDragOver={(e) => { e.preventDefault(); setIsDraggingTrigger(true); }}
                                             onDragLeave={() => setIsDraggingTrigger(false)}
                                             onDrop={handleDropTrigger}
                                             className={`flex flex-col items-center group cursor-pointer transition-all ${isDraggingTrigger ? 'scale-110' : ''}`}
                                          >
                                             <div className="bg-white px-4 py-1.5 rounded-full border border-slate-200 shadow-sm text-[9px] font-black uppercase tracking-widest text-slate-400 mb-3">Trigger</div>
                                             <div className={`w-20 h-20 rounded-full flex items-center justify-center shadow-xl border-4 transition-all ${isDraggingTrigger ? 'bg-red-100 border-red-400 animate-pulse' : 'bg-white border-slate-100 hover:border-red-200'}`}>
                                                {TRIGGERS.find(t => t.id === editingFlow.trigger)?.icon || <Zap size={32} />}
                                             </div>
                                             <div className="mt-3 text-center">
                                                <p className="text-sm font-bold text-slate-900">{TRIGGERS.find(t => t.id === editingFlow.trigger)?.label}</p>
                                                <p className="text-[10px] text-slate-400">{TRIGGERS.find(t => t.id === editingFlow.trigger)?.description}</p>
                                             </div>

                                             {/* Custom Event Configuration */}
                                             {editingFlow.trigger === 'custom_event' && (
                                                <div className="mt-4 p-3 bg-white border border-slate-200 rounded-xl shadow-sm w-full max-w-xs" onClick={e => e.stopPropagation()}>
                                                   <div className="flex justify-between items-center mb-2">
                                                      <label className="text-[9px] font-black uppercase tracking-widest text-slate-400">Custom Event Hook Naam</label>
                                                      <button
                                                         onClick={async (e) => {
                                                            e.stopPropagation();
                                                            const goal = prompt("Wat wil je precies als event? (bijv. 'als een gebruiker een hoge testscore heeft beholad')");
                                                            if (!goal) return;
                                                            setIsGeneratingTrigger(true);
                                                            try {
                                                               const apiUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
                                                                  ? '/api/ai/froukje/chat'
                                                                  : `${window.location.protocol}//${window.location.hostname}:4000/api/ai/froukje/chat`;
                                                               const res = await fetch(apiUrl, {
                                                                  method: 'POST',
                                                                  headers: { 'Content-Type': 'application/json' },
                                                                  body: JSON.stringify({
                                                                     prompt: `Geef mij één korte, lower_case_snake_case string terug die dit event beschrijft: ${goal}. Geen quotes, geen uitleg. Meteen alleen de slug.`,
                                                                     history: []
                                                                  })
                                                               });
                                                               if (res.ok) {
                                                                  const data = await res.json();
                                                                  if (data.message) setEditingFlow({ ...editingFlow, trigger_config: { ...editingFlow.trigger_config, customEventName: data.message.trim() } });
                                                               }
                                                            } catch (err) { console.error(err) } finally { setIsGeneratingTrigger(false); }
                                                         }}
                                                         className="flex items-center gap-1.5 px-2 py-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-md text-[9px] font-black uppercase tracking-wider hover:shadow-md transition-all disabled:opacity-50"
                                                         disabled={isGeneratingTrigger}
                                                      >
                                                         {isGeneratingTrigger ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />} Vraag AI
                                                      </button>
                                                   </div>
                                                   <input
                                                      type="text"
                                                      value={editingFlow.trigger_config?.customEventName || ''}
                                                      onChange={e => setEditingFlow({
                                                         ...editingFlow,
                                                         trigger_config: { ...editingFlow.trigger_config, customEventName: e.target.value }
                                                      })}
                                                      className="w-full text-xs font-mono p-2 bg-slate-50 border border-slate-200 rounded outline-none focus:border-slate-400"
                                                      placeholder="bijv: user_score_high"
                                                   />
                                                </div>
                                             )}

                                             {/* Stage Changed Configuration */}
                                             {editingFlow.trigger === 'stage_changed' && (
                                                <div className="mt-4 p-3 bg-white border border-slate-200 rounded-xl shadow-sm w-full max-w-xs" onClick={e => e.stopPropagation()}>
                                                   <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-2">Specifieke Fase (Optioneel)</label>
                                                   <select
                                                      value={editingFlow.trigger_config?.targetStageName || ''}
                                                      onChange={e => setEditingFlow({
                                                         ...editingFlow,
                                                         trigger_config: { ...editingFlow.trigger_config, targetStageName: e.target.value }
                                                      })}
                                                      className="w-full text-xs font-bold p-2 bg-slate-50 border border-slate-200 rounded outline-none focus:border-slate-400"
                                                   >
                                                      <option value="">Alle Fases</option>
                                                      <option value="Lead">Lead</option>
                                                      <option value="Active">Active</option>
                                                      <option value="Interview">Interview</option>
                                                      <option value="Placed">Placed</option>
                                                      <option value="Rejected">Rejected</option>
                                                   </select>
                                                   <p className="text-[9px] text-slate-400 mt-2">Laat leeg om op elke fase-wijziging te triggeren.</p>
                                                </div>
                                             )}

                                             <div className="h-12 w-0.5 bg-slate-300 my-2 relative">
                                                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-slate-300 rounded-full"></div>
                                             </div>
                                          </div>
                                       )}

                                       {/* Render Steps Helper */}
                                       {(() => {
                                          const renderStepList = (steps: AutomationStep[], parentBranchId?: string) => {
                                             return steps.map((step, idx) => {
                                                const color = getStepColor(step.type);
                                                const isSelected = selectedNodeId === step.id;

                                                return (
                                                   <React.Fragment key={step.id}>
                                                      <div
                                                         onClick={(e) => { e.stopPropagation(); setSelectedNodeId(step.id); }}
                                                         draggable onDragStart={e => handleDragStartStep(e, step.id)} onDragEnd={handleDragEnd}
                                                         className={`w-[320px] xl:w-[380px] 2xl:w-[450px] bg-white border-2 rounded-2xl p-4 xl:p-5 shadow-sm relative group/step cursor-pointer transition-all hover:shadow-lg flex-shrink-0 z-20 ${isSelected ? `border-${color}-500 ring-2 ring-${color}-100` : `border-slate-100 hover:border-${color}-200`}`}
                                                      >
                                                         <div className="absolute top-4 right-4 flex gap-1 opacity-0 group-hover/step:opacity-100 transition-opacity z-30">
                                                            <div className="p-1.5 text-slate-300 hover:text-slate-600 cursor-grab"><GripVertical size={16} /></div>
                                                            <div onClick={(e) => { e.stopPropagation(); removeStepDeep(step.id); }} className="p-1.5 text-slate-300 hover:text-red-500 cursor-pointer"><Trash2 size={16} /></div>
                                                         </div>
                                                         <div className="flex items-start gap-4">
                                                            <div className={`p-3 rounded-xl bg-${color}-50 text-${color}-600`}>
                                                               {getStepIcon(step.type)}
                                                            </div>
                                                            <div>
                                                               <h4 className="font-bold text-sm text-slate-900">{step.label}</h4>
                                                               <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wide mt-1">Stap</p>
                                                               {step.type === 'email' && <p className="text-[10px] text-slate-500 mt-1 italic">Template: {templates.find(t => t.id === step.config.templateId)?.name || 'None'}</p>}
                                                               {step.type === 'condition' && <p className="text-[10px] text-orange-600 mt-1 font-bold bg-orange-50 px-2 py-0.5 rounded-md inline-block">Filter Logic</p>}
                                                               {step.type === 'branch' && <p className="text-[10px] text-indigo-600 mt-1 font-bold bg-indigo-50 px-2 py-0.5 rounded-md inline-block flex items-center gap-1 w-max"><GitBranch size={10} /> {step.config.branches?.length || 0} Vertakkingen</p>}
                                                            </div>
                                                         </div>

                                                         {/* Render Inline Branch Paths */}
                                                         {step.type === 'branch' && (
                                                            <div className="mt-8 relative flex items-start gap-6 isolation-auto -mx-10 scale-[0.90] origin-top">
                                                               {/* Horizontal connector */}
                                                               {(step.config.branches?.length || 0) > 1 && (
                                                                  <div className="absolute top-0 left-[25%] right-[25%] h-[2px] bg-slate-200"></div>
                                                               )}
                                                               {step.config.branches?.map(branch => (
                                                                  <div key={branch.id} className="relative flex flex-col items-center flex-1 min-w-[300px] xl:min-w-[360px] 2xl:min-w-[420px]">
                                                                     {/* Vertical Line Drop into branch */}
                                                                     <div className="absolute top-0 w-[2px] h-4 bg-slate-200"></div>
                                                                     <div className="px-6 py-4 bg-white border-2 border-slate-200 shadow-sm rounded-2xl flex flex-col items-center justify-between z-10 mt-4 cursor-default w-full">
                                                                        <span className="text-[11px] font-black uppercase tracking-widest text-slate-700 w-full text-center">{branch.name}</span>
                                                                     </div>

                                                                     <div className="flex flex-col items-center w-full mt-2">
                                                                        <div
                                                                           className="relative flex flex-col items-center w-full group/add pb-2 pt-2"
                                                                           onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                                                           onDrop={(e) => handleDropCanvas(e, 0, branch.id)}
                                                                        >
                                                                           <div className="h-8 w-[2px] bg-slate-200"></div>
                                                                           <button
                                                                              onClick={(e) => { e.stopPropagation(); setAddStepIndex({ index: 0, branchId: branch.id }); }}
                                                                              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white border border-slate-200 shadow-sm rounded-full flex items-center justify-center text-indigo-500 hover:text-white hover:bg-indigo-500 hover:border-solid transition-all opacity-100 z-10 hover:scale-110"
                                                                           >
                                                                              <Plus size={16} strokeWidth={3} />
                                                                           </button>
                                                                        </div>

                                                                        {/* Recursive steps inside the branch */}
                                                                        <div className="flex flex-col items-center w-full shrink-0 relative">
                                                                           {renderStepList(branch.steps || [], branch.id)}
                                                                        </div>

                                                                        {(!branch.steps || branch.steps.length === 0) && (
                                                                           <div className="h-10 w-[2px] bg-slate-200"></div>
                                                                        )}
                                                                     </div>
                                                                  </div>
                                                               ))}
                                                            </div>
                                                         )}
                                                      </div>

                                                      {/* Normal interstice sequence */}
                                                      <div
                                                         className="relative flex flex-col items-center w-full group/add pt-2 pb-2"
                                                         onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                                         onDrop={(e) => handleDropCanvas(e, idx + 1, parentBranchId)}
                                                      >
                                                         <div className="h-10 w-[2px] bg-slate-300 transition-all"></div>
                                                         <button
                                                            onClick={(e) => { e.stopPropagation(); setAddStepIndex({ index: idx + 1, branchId: parentBranchId }); }}
                                                            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white border border-slate-200 shadow-sm rounded-full flex items-center justify-center text-indigo-500 hover:text-white hover:bg-indigo-500 hover:border-solid transition-all opacity-100 z-10 hover:scale-110"
                                                         >
                                                            <Plus size={16} strokeWidth={3} />
                                                         </button>
                                                      </div>
                                                   </React.Fragment>
                                                )
                                             });
                                          };

                                          return (
                                             <>
                                                <div
                                                   className="relative flex flex-col items-center w-full group/add pt-2 pb-2"
                                                   onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                                   onDrop={(e) => handleDropCanvas(e, 0)}
                                                >
                                                   <div className="h-10 w-[2px] bg-slate-300 transition-all"></div>
                                                   <button
                                                      onClick={(e) => { e.stopPropagation(); setAddStepIndex({ index: 0 }); }}
                                                      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white border border-slate-200 shadow-sm rounded-full flex items-center justify-center text-indigo-500 hover:text-white hover:bg-indigo-500 hover:border-solid transition-all opacity-100 z-10 hover:scale-110"
                                                   >
                                                      <Plus size={16} strokeWidth={3} />
                                                   </button>
                                                </div>
                                                {renderStepList(editingFlow.steps || [])}
                                             </>
                                          );
                                       })()}

                                       <div className="w-12 h-12 rounded-full bg-slate-100 border-4 border-white shadow-md flex items-center justify-center text-slate-300">
                                          <div className="w-3 h-3 bg-slate-300 rounded-sm"></div>
                                       </div>
                                    </div>
                                 </div>
                              )}

                              {/* Flow Specific Logs */}
                              {editingFlowTab === 'logs' && (
                                 <div className="flex-1 overflow-y-auto p-8 pt-32 custom-scrollbar bg-slate-50/50">
                                    {logs.filter(l => l.flow_id === editingFlow.id || l.flowName === editingFlow.name).length === 0 ? (
                                       <div className="flex flex-col items-center justify-center h-full text-slate-400">
                                          <Activity size={48} className="mb-4 opacity-20" />
                                          <p className="text-xs font-black uppercase tracking-widest">Nog geen activiteiten voor deze flow</p>
                                       </div>
                                    ) : (
                                       <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden">
                                          <table className="w-full text-left">
                                             <thead className="bg-slate-50 border-b border-slate-100">
                                                <tr>
                                                   <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Trigger</th>
                                                   <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Tijdstip</th>
                                                   <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Status</th>
                                                   <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Stappen</th>
                                                </tr>
                                             </thead>
                                             <tbody>
                                                {logs.filter(l => l.flow_id === editingFlow.id || l.flowName === editingFlow.name).map((log, i) => (
                                                   <React.Fragment key={i}>
                                                      <tr onClick={() => setExpandedLogIdx(expandedLogIdx === i ? null : i)} className="border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer">
                                                         <td className="px-6 py-4 text-xs font-bold text-slate-600 flex items-center gap-2">
                                                            <ChevronRight size={14} className={`text-slate-400 transition-transform ${expandedLogIdx === i ? 'rotate-90' : ''}`} />
                                                            {log.triggerEvent}
                                                         </td>
                                                         <td className="px-6 py-4 text-xs text-slate-400 font-mono">{log.timestamp}</td>
                                                         <td className="px-6 py-4">
                                                            <span className={`px-2 py-1 rounded text-[10px] font-black uppercase tracking-wide border ${log.status === 'success' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-red-50 text-red-600 border-red-100'}`}>
                                                               {log.status}
                                                            </span>
                                                         </td>
                                                         <td className="px-6 py-4 text-xs font-bold">{log.stepCount}</td>
                                                      </tr>
                                                      {expandedLogIdx === i && (
                                                         <tr className="bg-slate-50/50 border-b border-slate-100">
                                                            <td colSpan={4} className="px-10 py-6">
                                                               <div className="space-y-4">
                                                                  <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400">Execution Trace Heatmap</h4>
                                                                  {log.executionTrace?.length > 0 ? (
                                                                     <div className="flex flex-col space-y-3">
                                                                        {log.executionTrace.map((trace: any, tIdx: number) => (
                                                                           <div key={tIdx} className="flex items-start gap-4">
                                                                              <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${trace.status === 'success' ? 'bg-emerald-100 text-emerald-600' : trace.status === 'failed' ? 'bg-red-100 text-red-600' : 'bg-slate-200 text-slate-400'}`}>
                                                                                 {trace.status === 'success' ? <CheckCircle2 size={12} strokeWidth={3} /> : trace.status === 'failed' ? <X size={12} strokeWidth={3} /> : <Minus size={12} strokeWidth={3} />}
                                                                              </div>
                                                                              <div>
                                                                                 <div className="flex items-center gap-2">
                                                                                    <span className="text-[10px] font-black uppercase tracking-wider text-slate-700">{trace.label || trace.type}</span>
                                                                                    <span className="text-[9px] text-slate-400 font-mono">{new Date(trace.timestamp).toLocaleTimeString('nl-NL')}</span>
                                                                                 </div>
                                                                                 <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{trace.details}</p>
                                                                              </div>
                                                                           </div>
                                                                        ))}
                                                                     </div>
                                                                  ) : (
                                                                     <p className="text-xs text-slate-400 italic">Pre-v2 logs hebben geen gedetailleerde trace beschikbaar.</p>
                                                                  )}
                                                               </div>
                                                            </td>
                                                         </tr>
                                                      )}
                                                   </React.Fragment>
                                                ))}
                                             </tbody>
                                          </table>
                                       </div>
                                    )}
                                 </div>
                              )}
                           </div>

                           {/* Right Panel (Config) */}
                           {editingFlowTab === 'canvas' && selectedNodeId && findStepDeep(editingFlow.steps || [], selectedNodeId) && (
                              <div className="w-96 xl:w-[400px] bg-white border-l border-slate-200 z-20 flex flex-col shadow-xl animate-in slide-in-from-right duration-200">
                                 <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/80">
                                    <h3 className="text-xs font-black uppercase tracking-widest text-slate-900">Configure Step</h3>
                                    <button onClick={() => setSelectedNodeId(null)}><X size={16} className="text-slate-400 hover:text-slate-900" /></button>
                                 </div>
                                 <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
                                    {(() => {
                                       const step = findStepDeep(editingFlow.steps || [], selectedNodeId)!;

                                       return (
                                          <>
                                             <div>
                                                <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-2">Label</label>
                                                <input type="text" value={step.label} onChange={e => updateStepConfigDeep(step.id, { label: e.target.value })} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold outline-none focus:border-slate-400" />
                                             </div>

                                             {step.type === 'email' && (
                                                <div className="space-y-4">
                                                   <div>
                                                      <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-2">Template (Optioneel)</label>
                                                      <select value={step.config.templateId || ''} onChange={e => updateStepConfigDeep(step.id, { templateId: e.target.value })} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold outline-none">
                                                         <option value="">Selecteer Template...</option>
                                                         {templates.filter(t => t.type === 'Email').map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                                      </select>
                                                   </div>

                                                   <div className="grid grid-cols-2 gap-3">
                                                      <div>
                                                         <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-2">Afzender (Van)</label>
                                                         <input type="text" value={step.config.from || user?.email || ''} placeholder="Naam of Email" onChange={e => updateStepConfigDeep(step.id, { from: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium outline-none" />
                                                      </div>
                                                      <div>
                                                         <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-2">Ontvanger (Aan)</label>
                                                         <input type="text" value={step.config.to || '{{candidate.email}}'} onChange={e => updateStepConfigDeep(step.id, { to: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium outline-none" />
                                                      </div>
                                                   </div>

                                                   <div className="grid grid-cols-2 gap-3">
                                                      <div className="col-span-2">
                                                         <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-2">Verzendoptie</label>
                                                         <select value={step.config.sendMode || 'auto'} onChange={e => updateStepConfigDeep(step.id, { sendMode: e.target.value as any })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold outline-none text-slate-600">
                                                            <option value="auto">⚡ Automatisch Verzenden</option>
                                                            <option value="manual">📝 Zet Klaar (Concept)</option>
                                                         </select>
                                                      </div>
                                                   </div>

                                                   <div>
                                                      <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-2">Onderwerp</label>
                                                      <input type="text" value={step.config.subject || ''} placeholder="Onderwerp..." onChange={e => updateStepConfigDeep(step.id, { subject: e.target.value })} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold outline-none" />
                                                   </div>

                                                   <div>
                                                      <div className="flex justify-between items-center mb-2">
                                                         <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block pl-1">Bericht</label>
                                                         <button onClick={() => { setAiTargetStepId(step.id); setShowAiModal(true); }} className="flex items-center gap-1.5 px-2 py-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-md text-[9px] font-black uppercase tracking-wider hover:shadow-md transition-all">
                                                            <BrainCircuit size={10} /> AI Schrijven
                                                         </button>
                                                      </div>
                                                      <RichTextEditor value={step.config.customMessage || ''} onChange={val => updateStepConfigDeep(step.id, { customMessage: val })} placeholder="Schrijf je email..." />
                                                      <div className="flex gap-1.5 mt-2 flex-wrap">
                                                         {[
                                                            '{{candidate.first_name}}', '{{candidate.last_name}}', '{{candidate.email}}', '{{candidate.phone}}',
                                                            '{{vacancy.title}}', '{{vacancy.company}}', '{{vacancy.location}}', '{{vacancy.type}}',
                                                            '{{recruiter.name}}', '{{recruiter.email}}', '{{recruiter.phone}}',
                                                            '{{company.name}}', '{{date.today}}', '{{date.tomorrow}}'
                                                         ].map(v => (
                                                            <span key={v} onClick={() => updateStepConfigDeep(step.id, { customMessage: (step.config.customMessage || '') + ' ' + v })} className="px-2 py-1 bg-slate-100 border border-slate-200 rounded text-[9px] font-mono text-slate-500 cursor-pointer hover:bg-slate-200 transition-colors">{v}</span>
                                                         ))}
                                                      </div>
                                                   </div>
                                                </div>
                                             )}

                                             {step.type === 'branch' && (
                                                <div className="space-y-6">
                                                   <div className="p-3 bg-indigo-50 border border-indigo-100 rounded-xl">
                                                      <p className="text-[10px] font-medium text-indigo-800 leading-relaxed">
                                                         Bouw splitsingen met uitgebreide filters. Kandidaten stromen in de eerste branch waar ze aan voldoen.
                                                      </p>
                                                   </div>

                                                   {/* Render branches */}
                                                   {step.config.branches?.map((branch, bIdx) => (
                                                      <div key={branch.id} className="p-4 border border-slate-200 rounded-xl bg-white shadow-sm space-y-4 relative group">
                                                         {bIdx > 0 && (
                                                            <button onClick={() => updateStepConfigDeep(step.id, { branches: step.config.branches?.filter(b => b.id !== branch.id) })} className="absolute top-2 right-2 p-1.5 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"><Trash2 size={14} /></button>
                                                         )}
                                                         <div>
                                                            <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-1">Naam Vertakking</label>
                                                            <input type="text" value={branch.name} onChange={e => {
                                                               const branches = [...(step.config.branches || [])];
                                                               branches[bIdx] = { ...branches[bIdx], name: e.target.value };
                                                               updateStepConfigDeep(step.id, { branches });
                                                            }} className="w-full p-2 bg-slate-50 border border-slate-200 rounded text-xs font-bold outline-none" />
                                                         </div>

                                                         {/* Condition Groups */}
                                                         <div className="space-y-3 pt-2 border-t border-slate-100">
                                                            {branch.conditionGroups?.map((group, gIdx) => (
                                                               <div key={group.id} className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-3 relative">
                                                                  <div className="flex justify-between items-center">
                                                                     <select value={group.type} onChange={e => {
                                                                        const branches = [...(step.config.branches || [])];
                                                                        branches[bIdx].conditionGroups[gIdx].type = e.target.value as any;
                                                                        updateStepConfigDeep(step.id, { branches });
                                                                     }} className="text-[9px] font-black uppercase bg-white border border-slate-200 rounded px-2 py-1 outline-none text-indigo-600">
                                                                        <option value="and">Allemaal (AND)</option>
                                                                        <option value="or">Eén van (OR)</option>
                                                                     </select>
                                                                     <button onClick={() => {
                                                                        const branches = [...(step.config.branches || [])];
                                                                        branches[bIdx].conditionGroups = branches[bIdx].conditionGroups.filter(g => g.id !== group.id);
                                                                        updateStepConfigDeep(step.id, { branches });
                                                                     }} className="text-slate-400 hover:text-red-500"><X size={12} /></button>
                                                                  </div>

                                                                  {/* Conditions */}
                                                                  {group.conditions.map((cond, cIdx) => (
                                                                     <div key={cIdx} className="grid grid-cols-1 gap-2 bg-white p-2 border border-slate-100 rounded relative">
                                                                        <button onClick={() => {
                                                                           const branches = JSON.parse(JSON.stringify(step.config.branches));
                                                                           branches[bIdx].conditionGroups[gIdx].conditions.splice(cIdx, 1);
                                                                           updateStepConfigDeep(step.id, { branches });
                                                                        }} className="absolute top-1 right-1 text-slate-300 hover:text-red-500"><X size={10} /></button>

                                                                        <select value={cond.field} onChange={e => {
                                                                           const branches = JSON.parse(JSON.stringify(step.config.branches));
                                                                           branches[bIdx].conditionGroups[gIdx].conditions[cIdx].field = e.target.value;
                                                                           updateStepConfigDeep(step.id, { branches });
                                                                        }} className="w-full text-[10px] font-bold p-1.5 bg-slate-50 border border-slate-200 rounded outline-none w-[calc(100%-16px)]">
                                                                           <option value="">Selecteer veld...</option>
                                                                           {Object.entries(FILTER_OPTIONS).map(([cat, fields]) => (
                                                                              <optgroup key={cat} label={cat}>
                                                                                 {fields.map(f => <option key={cat + '.' + f.field} value={`${cat}.${f.field}`}>{f.label}</option>)}
                                                                              </optgroup>
                                                                           ))}
                                                                        </select>

                                                                        <div className="grid grid-cols-2 gap-2">
                                                                           <select value={cond.operator} onChange={e => {
                                                                              const branches = JSON.parse(JSON.stringify(step.config.branches));
                                                                              branches[bIdx].conditionGroups[gIdx].conditions[cIdx].operator = e.target.value;
                                                                              updateStepConfigDeep(step.id, { branches });
                                                                           }} className="w-full text-[9px] font-bold p-1.5 bg-slate-50 border border-slate-200 rounded outline-none">
                                                                              <option value="equals">Gelijk aan</option>
                                                                              <option value="contains">Bevat</option>
                                                                              <option value="greater_than">Groter dan</option>
                                                                              <option value="is_set">Is gevuld</option>
                                                                           </select>
                                                                           {cond.operator !== 'is_set' && (
                                                                              <input type="text" value={cond.value} onChange={e => {
                                                                                 const branches = JSON.parse(JSON.stringify(step.config.branches));
                                                                                 branches[bIdx].conditionGroups[gIdx].conditions[cIdx].value = e.target.value;
                                                                                 updateStepConfigDeep(step.id, { branches });
                                                                              }} className="w-full text-[10px] p-1.5 bg-slate-50 border border-slate-200 rounded outline-none" placeholder="Waarde..." />
                                                                           )}
                                                                        </div>
                                                                     </div>
                                                                  ))}

                                                                  <button onClick={() => {
                                                                     const branches = JSON.parse(JSON.stringify(step.config.branches));
                                                                     branches[bIdx].conditionGroups[gIdx].conditions.push({ field: '', operator: 'equals', value: '' });
                                                                     updateStepConfigDeep(step.id, { branches });
                                                                  }} className="text-[9px] font-bold text-indigo-500 hover:text-indigo-700">+ Filter toevoegen</button>
                                                               </div>
                                                            ))}

                                                            <button onClick={() => {
                                                               const branches = JSON.parse(JSON.stringify(step.config.branches));
                                                               branches[bIdx].conditionGroups.push({ id: Math.random().toString(), type: 'and', conditions: [] });
                                                               updateStepConfigDeep(step.id, { branches });
                                                            }} className="w-full py-1.5 border border-dashed border-indigo-200 rounded-lg text-[9px] font-bold text-indigo-500 hover:bg-indigo-50">+ Conditiegroep</button>
                                                         </div>
                                                      </div>
                                                   ))}

                                                   <button onClick={() => updateStepConfigDeep(step.id, { branches: [...(step.config.branches || []), { id: Math.random().toString(), name: 'Nieuwe Vertakking', conditionGroups: [], steps: [] }] })} className="w-full p-3 border-2 border-dashed border-slate-200 rounded-xl text-xs font-bold text-slate-400 hover:text-slate-600 hover:border-slate-300 transition-all">+ Vertakking Toevoegen</button>
                                                </div>
                                             )}

                                             {step.type === 'condition' && (
                                                <div className="space-y-4">
                                                   <div className="p-3 bg-orange-50 border border-orange-100 rounded-xl">
                                                      <p className="text-[10px] font-medium text-orange-800 leading-relaxed">
                                                         Als deze conditie <strong>niet</strong> waar is, stopt de flow hier.
                                                      </p>
                                                   </div>
                                                   <div>
                                                      <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-2">Field</label>
                                                      <select value={step.config.conditionField || ''} onChange={e => updateStepConfigDeep(step.id, { conditionField: e.target.value, conditionValue: '' })} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold outline-none">
                                                         <option value="">Selecteer veld...</option>
                                                         {Object.entries(FILTER_OPTIONS).map(([cat, fields]) => (
                                                            <optgroup key={cat} label={cat}>
                                                               {fields.map(f => <option key={cat + '.' + f.field} value={`${cat}.${f.field}`}>{f.label}</option>)}
                                                            </optgroup>
                                                         ))}
                                                      </select>
                                                   </div>
                                                   <div>
                                                      <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-2">Operator</label>
                                                      <select value={step.config.conditionOperator || 'equals'} onChange={e => updateStepConfigDeep(step.id, { conditionOperator: e.target.value })} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold outline-none">
                                                         <option value="equals">Equals</option>
                                                         <option value="contains">Contains</option>
                                                         <option value="greater_than">Greater Than</option>
                                                         <option value="is_set">Is Set (Has Value)</option>
                                                      </select>
                                                   </div>
                                                   {(!step.config.conditionOperator || step.config.conditionOperator !== 'is_set') && (
                                                      <div>
                                                         <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-2">Value</label>
                                                         {(!step.config.conditionField || step.config.conditionField.includes('stage')) ? (
                                                            <select value={step.config.conditionValue || ''} onChange={e => updateStepConfigDeep(step.id, { conditionValue: e.target.value })} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold outline-none">
                                                               <option value="">Select Phase...</option>
                                                               <option value="Lead">Lead</option>
                                                               <option value="Active">Active</option>
                                                               <option value="Interview">Interview</option>
                                                               <option value="Placed">Placed</option>
                                                               <option value="Rejected">Rejected</option>
                                                            </select>
                                                         ) : (
                                                            <input type="text" value={step.config.conditionValue || ''} onChange={e => updateStepConfigDeep(step.id, { conditionValue: e.target.value })} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold outline-none" placeholder="e.g. Value..." />
                                                         )}
                                                      </div>
                                                   )}
                                                </div>
                                             )}

                                             {(step.type === 'sms' || step.type === 'whatsapp') && (
                                                <div className="space-y-4">
                                                   <div className="grid grid-cols-2 gap-3">
                                                      <div>
                                                         <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-2">Afzender</label>
                                                         <input type="text" value={step.config.from || (step.type === 'whatsapp' ? 'Bedrijf WhatsApp' : 'Werkplaats')} onChange={e => updateStepConfigDeep(step.id, { from: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium outline-none" />
                                                      </div>
                                                      <div>
                                                         <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-2">Ontvanger</label>
                                                         <input type="text" value={step.config.to || '{{candidate.phone}}'} onChange={e => updateStepConfigDeep(step.id, { to: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium outline-none" />
                                                      </div>
                                                   </div>

                                                   <div>
                                                      <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-2">Verzendoptie</label>
                                                      <select value={step.config.sendMode || 'auto'} onChange={e => updateStepConfigDeep(step.id, { sendMode: e.target.value as any })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold outline-none text-slate-600">
                                                         <option value="auto">⚡ Automatisch Verzenden</option>
                                                         <option value="manual">📝 Zet Klaar (Concept)</option>
                                                      </select>
                                                   </div>

                                                   <div>
                                                      <div className="flex justify-between items-center mb-2">
                                                         <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block pl-1">Bericht</label>
                                                         <button onClick={() => { setAiTargetStepId(step.id); setShowAiModal(true); }} className="flex items-center gap-1.5 px-2 py-1 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-md text-[9px] font-black uppercase tracking-wider hover:shadow-md transition-all">
                                                            <BrainCircuit size={10} /> AI Schrijven
                                                         </button>
                                                      </div>
                                                      <textarea value={step.config.customMessage || ''} onChange={e => updateStepConfigDeep(step.id, { customMessage: e.target.value })} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium h-32 resize-none outline-none mb-2" placeholder="Typ bericht..."></textarea>
                                                      <div className="flex gap-1.5 flex-wrap outline-none">
                                                         {[
                                                            '{{candidate.first_name}}', '{{candidate.last_name}}', '{{candidate.email}}', '{{candidate.phone}}',
                                                            '{{vacancy.title}}', '{{vacancy.company}}', '{{vacancy.location}}', '{{vacancy.type}}',
                                                            '{{recruiter.name}}', '{{recruiter.email}}', '{{recruiter.phone}}',
                                                            '{{company.name}}', '{{date.today}}', '{{date.tomorrow}}'
                                                         ].map(v => (
                                                            <span key={v} onClick={() => updateStepConfigDeep(step.id, { customMessage: (step.config.customMessage || '') + ' ' + v })} className="px-2 py-1 bg-slate-100 border border-slate-200 rounded text-[9px] font-mono text-slate-500 cursor-pointer hover:bg-slate-200 transition-colors">{v}</span>
                                                         ))}
                                                      </div>
                                                   </div>
                                                </div>
                                             )}

                                             {step.type === 'wait' && (
                                                <div className="flex gap-2">
                                                   <input type="number" value={step.config.delay} onChange={e => updateStepConfigDeep(step.id, { delay: e.target.value })} className="w-20 p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold outline-none text-center" />
                                                   <select value={step.config.delayUnit} onChange={e => updateStepConfigDeep(step.id, { delayUnit: e.target.value })} className="flex-1 p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold outline-none">
                                                      <option value="minutes">Minutes</option>
                                                      <option value="hours">Hours</option>
                                                      <option value="days">Days</option>
                                                   </select>
                                                </div>
                                             )}

                                             {step.type === 'docusign' && (
                                                <div className="space-y-4">
                                                   <div className="p-3 bg-red- border border-red- rounded-xl text-center">
                                                      <FileSignature size={24} className="mx-auto text-red-0 mb-2" />
                                                      <p className="text-[10px] font-bold text-red- leading-relaxed uppercase tracking-wider">
                                                         DocuSign Integratie
                                                      </p>
                                                      <p className="text-[9px] text-red- mt-1">Dit verstuurt een digitaal ondertekeningsverzoek naar de kandidaat.</p>
                                                   </div>
                                                   <div>
                                                      <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-2">Document Naam</label>
                                                      <input type="text" value={step.config.customMessage || ''} onChange={e => updateStepConfigDeep(step.id, { customMessage: e.target.value })} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold outline-none" placeholder="Bijv. Arbeidsovereenkomst" />
                                                   </div>
                                                </div>
                                             )}

                                             {step.type === 'n8n_webhook' && (
                                                <div className="space-y-4">
                                                   <div className="p-4 bg-orange-50/50 border border-orange-200 rounded-xl">
                                                      <div className="flex items-center gap-2 mb-2">
                                                         <Zap size={14} className="text-orange-500" />
                                                         <h4 className="text-[10px] font-black uppercase tracking-widest text-orange-800">Advanced: n8n Webhook</h4>
                                                      </div>
                                                      <p className="text-[10px] text-orange-700 leading-relaxed font-medium">
                                                         Hiermee triggert dit platform een externe n8n workflow zodra de kandidaat deze stap bereikt. Koppel dit aan een "Webhook Node" in je n8n workspace.
                                                      </p>
                                                   </div>
                                                   <div>
                                                      <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-2">n8n Webhook URL *</label>
                                                      <input type="text" value={step.config.webhookUrl || ''} onChange={e => updateStepConfigDeep(step.id, { webhookUrl: e.target.value })} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold outline-none focus:border-slate-400 transition-colors" placeholder="http://jouw-n8n.com/webhook/..." />
                                                   </div>
                                                   <div>
                                                      <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-2">Static JSON Payload (Optioneel)</label>
                                                      <textarea 
                                                         value={step.config.webhookPayload || ''} 
                                                         onChange={e => updateStepConfigDeep(step.id, { webhookPayload: e.target.value })} 
                                                         className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono h-24 resize-none outline-none focus:border-slate-400 transition-colors" 
                                                         placeholder='{"action": "send_welcome", "priority": "high"}'
                                                      />
                                                      <p className="text-[9px] text-slate-400 mt-2">De kandidaat data en vacature details worden hier <strong>automatisch</strong> door Werftools aan toegevoegd in de body.</p>
                                                   </div>
                                                </div>
                                             )}
                                          </>
                                       );
                                    })()}
                                 </div>
                              </div>
                           )}

                           {/* Right Sidebar: AI Chat */}
                           {showAiFlowModal && (
                              <AskFroukjeAutomationChat
                                 inline
                                 isOpen={showAiFlowModal}
                                 onClose={() => setShowAiFlowModal(false)}
                                 aiRecruiter={user?.branding?.aiRecruiter || { name: 'Froukje', description: 'Jouw intelligente Automation Architect en Process Designer.' }}
                                 onFlowGenerated={handleFlowGenerated}
                              />
                           )}
                        </div>
                     </div>
                  )}
               </div>
            )}
         </div>

         {/* AI GENERATION MODAL */}
         {showAiModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm animate-fadeIn">
               <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-lg w-full mx-4 border border-slate-200 animate-scaleIn relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-20 bg-purple-50 rounded-full blur-3xl -mr-10 -mt-10 pointer-events-none"></div>

                  <div className="flex items-center justify-between mb-6 relative z-10">
                     <h3 className="text-xl font-black text-slate-900 flex items-center gap-2">
                        <BrainCircuit className="text-purple-600" /> AI Template Writer
                     </h3>
                     <button onClick={() => { setShowAiModal(false); setAiTargetStepId(null); }} className="text-slate-400 hover:text-slate-900 transition-colors"><X size={20} /></button>
                  </div>

                  <div className="space-y-4 relative z-10">
                     <div>
                        <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-2">Instructie voor Froukje</label>
                        <textarea
                           value={aiPrompt}
                           onChange={e => setAiPrompt(e.target.value)}
                           placeholder="Bijv: Schrijf een vriendelijke maar duidelijke afwijzing voor een junior developer sollicitant, waarin we aangeven dat we iemand met meer ervaring zoeken."
                           className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium h-32 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                        />
                     </div>

                     <div className="pt-2 flex gap-3">
                        <button
                           onClick={() => setShowAiModal(false)}
                           className="flex-1 py-3 rounded-xl border border-slate-200 text-slate-600 font-bold text-xs uppercase tracking-wider hover:bg-slate-50 transition-colors"
                        >
                           Annuleren
                        </button>
                        <button
                           onClick={handleAiGenerate}
                           disabled={isGeneratingAi || !aiPrompt.trim()}
                           className="flex-1 py-3 rounded-xl bg-slate-900 text-white font-black text-xs uppercase tracking-wider hover:bg-black shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                           {isGeneratingAi ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Genereren
                        </button>
                     </div>
                  </div>
               </div>
            </div>
         )}

         {/* ADD STEP MODAL */}
         {addStepIndex !== null && (
            <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-200" onClick={() => setAddStepIndex(null)}>
               <div className="bg-white rounded-3xl shadow-xl w-[480px] overflow-hidden flex flex-col border border-slate-200 animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
                  <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                     <div>
                        <h2 className="text-lg font-black text-slate-900">Nieuwe Stap Toevoegen</h2>
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1">
                           Voeg een actie toe op positie {addStepIndex.index + 1}
                        </p>
                     </div>
                     <button onClick={() => setAddStepIndex(null)} className="p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-900 rounded-full transition-colors"><X size={20} /></button>
                  </div>
                  <div className="p-6 overflow-y-auto max-h-[60vh] custom-scrollbar">
                     <div className="grid grid-cols-2 gap-3">
                        {ACTIONS.map((action, i) => (
                           <button
                              key={action.type + i}
                              onClick={() => {
                                 addStepDeep(action.type as AutomationActionType, addStepIndex.index, addStepIndex.branchId);
                                 setAddStepIndex(null);
                              }}
                              className={`p-4 border border-slate-200 hover:border-${action.color}-300 hover:bg-${action.color}-50 flex items-center gap-3 rounded-2xl transition-all group`}
                           >
                              <div className={`p-2 rounded-lg bg-${action.color}-100 text-${action.color}-600 group-hover:bg-white transition-colors`}>
                                 {action.icon}
                              </div>
                              <div className="text-left">
                                 <h4 className="font-bold text-xs text-slate-700 group-hover:text-slate-900 transition-colors">{action.label}</h4>
                              </div>
                           </button>
                        ))}
                     </div>
                  </div>
               </div>
            </div>
         )}

         {/* DRAFT EDIT MODAL */}
         {editingDraft && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm animate-fadeIn">
               <div className="bg-white rounded-[2rem] shadow-2xl p-8 max-w-2xl w-full mx-4 border border-slate-200">
                  <div className="flex justify-between items-center mb-6">
                     <h3 className="text-lg font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
                        {editingDraft.type.includes('email') ? <Mail className="text-red-500" /> : <MessageCircle className="text-emerald-500" />}
                        Bewerk Concept Bericht
                     </h3>
                     <button onClick={() => setEditingDraft(null)} className="text-slate-400 hover:text-slate-900"><X size={20} /></button>
                  </div>
                  
                  <div className="space-y-4">
                     <div>
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-2">Aangepast Bericht</label>
                        <textarea 
                           className="w-full h-64 p-4 border border-slate-200 rounded-xl bg-slate-50 focus:bg-white text-sm font-medium outline-none focus:ring-2 focus:ring-red-500/20 transition-all resize-none"
                           value={editingDraft.content}
                           onChange={(e) => setEditingDraft({...editingDraft, content: e.target.value})}
                        />
                     </div>
                     <div className="flex gap-3 pt-4">
                        <button onClick={() => setEditingDraft(null)} className="flex-1 py-3 text-xs font-bold text-slate-500 hover:bg-slate-50 border border-slate-200 rounded-xl transition-colors">Annuleren</button>
                        <button onClick={async () => {
                           // Save to Supabase
                           await supabase.from('candidate_communications').update({ content: editingDraft.content }).eq('id', editingDraft.id);
                           setDrafts(drafts.map(d => d.id === editingDraft.id ? editingDraft : d));
                           setEditingDraft(null);
                        }} className="flex-1 py-3 text-xs font-black uppercase tracking-widest text-white bg-slate-900 hover:bg-black rounded-xl transition-colors shadow-lg hover:shadow-xl flex items-center justify-center gap-2">
                           <Save size={16} /> Opslaan
                        </button>
                     </div>
                  </div>
               </div>
            </div>
         )}
      </div>
   );
};

export default AutomationManager;
